import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getDirectoryHandle as getProjectDirHandle, saveDirectoryHandle as saveProjectDirHandle, removeDirectoryHandle as removeProjectDirHandle, walkDirectory } from '../utils/workingDirectory';
import { useAuth } from '../context/AuthContext';
import { useProjects } from '../hooks/useProjects';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Download, Printer, FileText, PenTool,
  Loader2, Trash2, Plus, SpellCheck, BookCheck, Shield,
  CheckCircle, AlertTriangle, Info, Copy, FolderOpen,
  FolderSync, RefreshCw, X as XIcon, ChevronDown, ChevronRight,
  Bold, Italic, Underline, Link2, Search, Hash, GripVertical
} from 'lucide-react';
import { CrewRefCardEditor, CrewTypeSelector, CrewDrillEditor, parseCrewXml, parseCrewDrill, normalizeCrewIds, normalizeLpCrewIds, collectCrewTargets } from './author/CrewEditors';
import { parseHotspotEl } from './author/HotspotEditor';
import XmlTreeView from './author/XmlTreeView';
import { RichParaEditor, rebuildInlineRefs, renderInlinePreview, getInternalRefTypes } from './author/RichParaEditor';
import { CalsTableEditor, parseCalsTable } from './author/CalsTableEditor';
import { CaptionGroupEditor, parseCaptionGroup } from './author/CaptionGroupEditor';
import { PmEditor, parsePmXml } from './author/PmEditor';
import { ElementEditor, LevelledParaBlock } from './author/LevelledParaEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QA_OPTIONS = [
  { value: 'Unverified', label: 'Unverified' },
  { value: 'First verification', label: 'First verification' },
  { value: 'Second verification', label: 'Second verification' },
  { value: 'Verified', label: 'Verified' },
];

const uid = () => Math.random().toString(36).substring(2, 10);

// Direct text of a node (excludes nested element text such as attentionRandomList)
const directText = (node) => Array.from(node.childNodes)
  .filter(n => n.nodeType === Node.TEXT_NODE)
  .map(n => n.textContent).join('').trim();

const parseAttnRandomItems = (rl) =>
  Array.from(rl.querySelectorAll(':scope > attentionRandomListItem > attentionListItemPara')).map(p => p.textContent?.trim() || '');

const parseDefinitionListEl = (dlEl) => ({
  items: Array.from(dlEl.querySelectorAll(':scope > definitionListItem')).map(dli => ({
    term: dli.querySelector(':scope > listItemTerm')?.textContent?.trim() || '',
    def: Array.from(dli.querySelectorAll(':scope > listItemDefinition > para')).map(p => p.textContent?.trim() || '').join('\n'),
  })),
});

// S1000D BRDP-S1-00101 id prefixes (Issue 5.0, Chap 3.9.5.2.1.2)
export const S1000D_ID_PREFIXES = {
  figure: 'fig',          // figures and alternates
  table: 'tab',           // tables
  multimedia: 'mma',      // multimedia and alternates
  supply: 'sup',          // supplies
  supportEquip: 'seq',    // support equipment
  spare: 'spa',           // spares
  levelledPara: 'par',    // levelled paragraphs and alternates
  step: 'stp',            // steps of procedure, fault isolation, etc
  graphic: 'gra',         // graphics (multiple sheets)
  multimediaObject: 'mmo',// multimedia objects
  hotspot: 'hot',         // hotspots (eg fig-0001-hot-0002)
  parameter: 'pme',       // parameters
  zone: 'zon',            // zones
  workLocation: 'wla',    // work locations
  material: 'mat',        // single material or material sets
  materialSetList: 'msl', // material set lists
  accessPoint: 'acp',     // access points
  footnote: 'ftn',        // footnotes (Chap 3.9.5.2.1.10)
};

// S1000D-compliant sequential ids (fig-0001, tab-0001, par-0001, ...) per data module
const nextSeqId = (prefix, lps) => {
  let max = 0;
  const re = new RegExp(`^${prefix}-(\\d{4,})$`);
  const check = (id) => { const m = re.exec(id || ''); if (m) max = Math.max(max, parseInt(m[1], 10)); };
  (lps || []).forEach(lp => {
    check(lp.id);
    (lp.elements || []).forEach(el => check(el.id));
  });
  return `${prefix}-${String(max + 1).padStart(4, '0')}`;
};

// BRDP-S1-00101: replace internal/legacy ids (random uid, step-NNN) with spec ids (stp-0001 / par-0001); preserve file-sourced ids
const isNonSpecLpId = (id) => !id || !id.includes('-') || /^step-\d+$/i.test(id);
const normalizeLpIds = (lps, prefix) => {
  const re = new RegExp(`^${prefix}-(\\d{4,})$`);
  let max = 0;
  (lps || []).forEach(lp => { const m = re.exec(lp.id || ''); if (m) max = Math.max(max, parseInt(m[1], 10)); });
  return (lps || []).map(lp => isNonSpecLpId(lp.id) ? { ...lp, id: `${prefix}-${String(++max).padStart(4, '0')}` } : lp);
};

// Deep-remap internal references ([Ref:xxx] markers and refId props) after an id renumber
const deepRemapRefs = (node, mapping) => {
  if (typeof node === 'string') return node.replace(/\[Ref:([^\]]+)\]/g, (m, id) => mapping[id] ? `[Ref:${mapping[id]}]` : m);
  if (Array.isArray(node)) return node.map(n => deepRemapRefs(n, mapping));
  if (node && typeof node === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(node)) {
      out[k] = (k === 'refId' && typeof v === 'string' && mapping[v]) ? mapping[v] : deepRemapRefs(v, mapping);
    }
    return out;
  }
  return node;
};

// After a reorder: reassign spec-pattern ids (stp-0001/par-0001) sequentially by document order
const renumberSpecIds = (lps, prefix) => {
  const re = new RegExp(`^${prefix}-\\d{4,}$`);
  let n = 0;
  const mapping = {};
  const next = lps.map(lp => {
    if (re.test(lp.id || '')) {
      n += 1;
      const nid = `${prefix}-${String(n).padStart(4, '0')}`;
      if (nid !== lp.id) mapping[lp.id] = nid;
      return nid === lp.id ? lp : { ...lp, id: nid };
    }
    return lp;
  });
  if (Object.keys(mapping).length === 0) return next;
  return next.map(lp => deepRemapRefs(lp, mapping));
};

/* ═══════════════════════════════════════════
   S1000D SCHEMA DETECTION FROM XSD
   ═══════════════════════════════════════════ */
const XSD_SCHEMA_MAP = {
  'descript.xsd': 'Descriptive',
  'proced.xsd': 'Procedural',
  'crew.xsd': 'Crew',
  'pm.xsd': 'Publication Module',
  'ddn.xsd': 'Data Dispatch Note',
  'dml.xsd': 'DML',
  'brex.xsd': 'BREX',
  'comrep.xsd': 'Comment',
  'fault.xsd': 'Fault',
  'ipd.xsd': 'IPD',
  'schedul.xsd': 'Schedule',
  'learning.xsd': 'Learning',
  'sb.xsd': 'Service Bulletin',
  'wrngdata.xsd': 'Wiring Data',
  'wrngflds.xsd': 'Wiring Fields',
  'condcrossref.xsd': 'Condition Cross Ref',
  'prdcrossref.xsd': 'Product Cross Ref',
  'appliccrossref.xsd': 'Applicability Cross Ref',
  'container.xsd': 'Container',
  'process.xsd': 'Process',
  'frontmatter.xsd': 'Front Matter',
  'checklist.xsd': 'Checklist',
  'sb.xsd': 'Service Bulletin',
  'update.xsd': 'Update',
};

const SUPPORTED_SCHEMAS = ['Descriptive', 'Procedural', 'Crew', 'Publication Module'];

function detectSchemaFromXml(xmlSnippet) {
  const match = xmlSnippet.match(/xsi:noNamespaceSchemaLocation\s*=\s*"([^"]+)"/);
  if (!match) return null;
  const url = match[1];
  const xsdFile = url.split('/').pop().toLowerCase();
  return XSD_SCHEMA_MAP[xsdFile] || xsdFile.replace('.xsd', '');
}

function isSchemaSupported(schemaLabel) {
  if (!schemaLabel) return true; // unknown schema defaults to open
  return SUPPORTED_SCHEMAS.some(s => s.toLowerCase() === schemaLabel.toLowerCase());
}

/* ═══════════════════════════════════════════
   INDEXEDDB HELPERS FOR DIRECTORY HANDLE
   ═══════════════════════════════════════════ */
/* ═══════════════════════════════════════════
   XML PREVIEW PANEL
   ═══════════════════════════════════════════ */
const XmlPreviewPanel = ({ projectId, dmc, title, schemaType, levelledParas, preliminaryRqmts, closeRqmts, crewType, crewTitle, crewDrills, pmEntries, security, rpc, issueNumber, inWork, qa, token, collapsedSections, activeKey, jump }) => {
  const [xml, setXml] = useState('');
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const fetchPreview = useCallback(async () => {
    if (!projectId || !dmc) { setXml(''); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/author/preview-xml`, {
        project_id: projectId,
        dmc, title, schema_type: schemaType,
        levelled_paras: levelledParas,
        preliminary_rqmts: schemaType === 'procedural' ? preliminaryRqmts : null,
        close_rqmts: schemaType === 'procedural' ? closeRqmts : null,
        crew_type: schemaType === 'crew' ? crewType : null,
        crew_title: schemaType === 'crew' ? crewTitle : '',
        crew_drills: schemaType === 'crew' ? crewDrills : [],
        pm_entries: schemaType === 'pm' ? pmEntries : [],
        content_blocks: [],
        security, rpc, issue_number: issueNumber, in_work: inWork, quality_assurance: qa,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setXml(res.data.xml || '');
    } catch { setXml('<!-- Error generating preview -->'); }
    setLoading(false);
  }, [projectId, dmc, title, schemaType, levelledParas, preliminaryRqmts, closeRqmts, crewType, crewTitle, crewDrills, pmEntries, security, rpc, issueNumber, inWork, qa, token]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchPreview, 600);
    return () => clearTimeout(debounceRef.current);
  }, [fetchPreview]);

  const copyXml = () => {
    navigator.clipboard.writeText(xml);
    toast.success('XML copied to clipboard');
  };

  const [showLineNumbers, setShowLineNumbers] = useState(() => localStorage.getItem('author-xml-line-numbers') === '1');
  const toggleLineNumbers = () => {
    setShowLineNumbers((v) => {
      localStorage.setItem('author-xml-line-numbers', v ? '0' : '1');
      return !v;
    });
  };

  return (
    <div className="flex flex-col h-full bg-slate-900 rounded-lg overflow-hidden" data-testid="xml-preview-panel">
      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-800 border-b border-slate-700">
        <span className="text-xs font-semibold text-slate-300 uppercase tracking-wider">XML Preview</span>
        <div className="flex items-center gap-2">
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
          <button
            onClick={toggleLineNumbers}
            title={showLineNumbers ? 'Hide line numbers' : 'Show line numbers'}
            className={`text-xs flex items-center gap-1 ${showLineNumbers ? 'text-emerald-400 hover:text-emerald-300' : 'text-slate-400 hover:text-slate-300'}`}
            data-testid="toggle-line-numbers-btn"
          >
            <Hash className="w-3 h-3" /> Lines
          </button>
          <button onClick={copyXml} className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1" data-testid="copy-xml-btn">
            <Copy className="w-3 h-3" /> Copy
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-auto p-4 text-xs font-mono leading-relaxed">
        {xml ? (
          <XmlTreeView
            xml={xml}
            schemaType={schemaType}
            lpKeys={(levelledParas || []).map((lp, i) => `lp-${lp.id || i}`)}
            drillKeys={(crewDrills || []).map((d, i) => `drill-${d.id || i}`)}
            syncCollapsed={collapsedSections || {}}
            activeKey={activeKey}
            showLineNumbers={showLineNumbers}
            jump={jump}
          />
        ) : (
          <pre className="text-green-400 whitespace-pre-wrap">{'<!-- Select a data module to see XML preview -->'}</pre>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   CHECKS INLINE PANEL
   ═══════════════════════════════════════════ */
const InlineChecks = ({ projectId, levelledParas, token, getModulePayload = null, onJumpToError = null }) => {
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState('');

  const getFullText = () => {
    let text = '';
    for (const lp of levelledParas) {
      if (lp.title) text += lp.title + ' ';
      for (const elem of (lp.elements || [])) {
        if (elem.content) text += elem.content + ' ';
        if (elem.items) text += elem.items.join(' ') + ' ';
        if (elem.inlineList?.items) text += elem.inlineList.items.join(' ') + ' ';
      }
    }
    return text
      .replace(/\[(?:DMRef|PMRef|ExtRef|Ref):[^\]]*\]/g, ' ')
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/__([^_]+)__/g, '$1')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const runSpellCheck = async () => {
    setChecking('spell');
    try {
      const res = await axios.post(`${API}/author/check/spell`, {
        text: getFullText(), project_id: projectId
      }, { headers: { Authorization: `Bearer ${token}` } });
      setResult({ type: 'spell', data: res.data });
      if ((res.data.issues?.length || 0) === 0) toast.success('No spelling errors found');
      else toast.warning(`${res.data.issues.length} spelling issue(s) found`);
    } catch { toast.error('Spell check failed'); }
    setChecking('');
  };

  const runSTECheck = async () => {
    setChecking('ste');
    try {
      const res = await axios.post(`${API}/ste-check/check`, {
        text: getFullText(), project_id: projectId
      }, { headers: { Authorization: `Bearer ${token}` } });
      setResult({ type: 'ste', data: res.data });
      if (res.data.compliant) toast.success('STE compliant');
      else toast.warning(`${res.data.violations?.length || 0} STE violation(s)`);
    } catch { toast.error('STE check failed'); }
    setChecking('');
  };

  const runSchemaCheck = async () => {
    if (!getModulePayload) return;
    setChecking('schema');
    try {
      const res = await axios.post(`${API}/author/check/schema`, getModulePayload(), { headers: { Authorization: `Bearer ${token}` } });
      setResult({ type: 'schema', data: res.data });
      if (res.data.valid) toast.success(`Schema valid (${res.data.schema_file})`);
      else toast.warning(`${res.data.errors?.length || 0} schema violation(s)`);
    } catch { toast.error('Schema check failed'); }
    setChecking('');
  };

  return (
    <div className="space-y-3" data-testid="checks-panel">
      <div className="flex gap-2">
        <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={runSpellCheck} disabled={!!checking} data-testid="spell-check-btn">
          {checking === 'spell' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <SpellCheck className="w-3.5 h-3.5 text-blue-600" />}
          Spell Check
        </Button>
        <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={runSTECheck} disabled={!!checking} data-testid="ste-check-btn">
          {checking === 'ste' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BookCheck className="w-3.5 h-3.5 text-teal-600" />}
          STE Check
        </Button>
        {getModulePayload && (
          <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={runSchemaCheck} disabled={!!checking} data-testid="schema-check-btn">
            {checking === 'schema' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />}
            Schema Check
          </Button>
        )}
        <Button variant="outline" size="sm" className="gap-1.5 text-xs" disabled data-testid="brex-check-btn">
          <Shield className="w-3.5 h-3.5 text-violet-600" />
          BREX Check
        </Button>
      </div>

      {result && result.type === 'schema' && (
        <div className={`p-3 rounded-lg text-sm ${result.data.valid ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`} data-testid="schema-check-result">
          {result.data.valid ? (
            <p className="text-emerald-700 flex items-center gap-1.5 font-medium"><CheckCircle className="w-4 h-4" /> Valid against S1000D Issue 5.0 ({result.data.schema_file})</p>
          ) : (
            <div className="space-y-1">
              <p className="font-medium text-red-800">{result.data.errors?.length || 0} schema violation(s) — {result.data.schema_file}</p>
              {result.data.errors?.slice(0, 15).map((err, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => err.path && onJumpToError && onJumpToError(err)}
                  className={`w-full text-left text-red-700 text-xs flex items-start gap-1 ${err.path && onJumpToError ? 'hover:underline hover:text-red-900 cursor-pointer' : 'cursor-default'}`}
                  title={err.path ? 'Click to locate in XML preview' : undefined}
                  data-testid={`schema-error-${i}`}
                >
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> Line {err.line}: {err.message}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {result && result.type === 'spell' && (
        <div className={`p-3 rounded-lg text-sm ${result.data.issues?.length === 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
          {result.data.issues?.length === 0 ? (
            <p className="text-emerald-700 flex items-center gap-1.5 font-medium"><CheckCircle className="w-4 h-4" /> All Clear — No spelling errors found</p>
          ) : (
            <div className="space-y-1">
              <p className="font-medium text-amber-800">{result.data.issues.length} issue(s)</p>
              {result.data.issues.map((issue, i) => (
                <p key={i} className="text-amber-700 text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> &quot;{issue.word}&quot; — {issue.suggestion}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {result && result.type === 'ste' && (
        <div className={`p-3 rounded-lg text-sm ${result.data.compliant ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
          {result.data.compliant ? (
            <p className="text-emerald-700 flex items-center gap-1.5 font-medium"><CheckCircle className="w-4 h-4" /> STE Compliant</p>
          ) : (
            <div className="space-y-1">
              <p className="font-medium text-amber-800">{result.data.violations?.length || 0} violation(s)</p>
              {result.data.violations?.slice(0, 10).map((v, i) => (
                <p key={i} className="text-amber-700 text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> &quot;{v.word}&quot; — {v.suggestion}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════
   PARSE PUBLICATION MODULE XML FROM WORKING DIR
   ═══════════════════════════════════════════════ */
function parsePmXmlFile(doc, rawXml) {
  const issueInfo = doc.querySelector('issueInfo');
  const secEl = doc.querySelector('security');
  const qaEl = doc.querySelector('qualityAssurance');
  const rpcEl = doc.querySelector('responsiblePartnerCompany enterpriseName');
  const titleEl = doc.querySelector('pmTitle');

  const meta = {
    issue_number: issueInfo?.getAttribute('issueNumber') || '001',
    in_work: issueInfo?.getAttribute('inWork') || '00',
    security: secEl?.getAttribute('securityClassification') || '01',
    rpc: rpcEl?.textContent || '',
    title: titleEl?.textContent || '',
  };

  if (qaEl?.querySelector('firstVerification')) meta.quality_assurance = 'First verification';
  else if (qaEl?.querySelector('secondVerification')) meta.quality_assurance = 'Second verification';
  else meta.quality_assurance = 'Unverified';

  const pmData = parsePmXml(doc);

  return {
    meta,
    levelledParas: [],
    rawXml,
    detectedSchema: 'pm',
    preliminaryRqmts: null,
    closeRqmts: null,
    crewData: null,
    pmEntries: pmData.pmEntries || [],
  };
}

/* ═══════════════════════════════════════════════
   PARSE S1000D XML FROM WORKING DIRECTORY FILE
   ═══════════════════════════════════════════════ */
async function parseXmlFile(fileHandle) {
  try {
    const file = await fileHandle.getFile();
    const text = await file.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, 'application/xml');

    // Check if this is a Publication Module (<pm> root)
    const pmRoot = doc.querySelector('pm');
    if (pmRoot) {
      return parsePmXmlFile(doc, text);
    }

    // Extract metadata
    const issueInfo = doc.querySelector('issueInfo');
    const secEl = doc.querySelector('security');
    const qaEl = doc.querySelector('qualityAssurance');
    const rpcEl = doc.querySelector('responsiblePartnerCompany enterpriseName');
    const titleEl = doc.querySelector('dmTitle techName');

    const meta = {
      issue_number: issueInfo?.getAttribute('issueNumber') || '001',
      in_work: issueInfo?.getAttribute('inWork') || '00',
      security: secEl?.getAttribute('securityClassification') || '01',
      rpc: rpcEl?.textContent || '',
      title: titleEl?.textContent || '',
    };

    // Determine QA
    if (qaEl?.querySelector('firstVerification')) meta.quality_assurance = 'First verification';
    else if (qaEl?.querySelector('secondVerification')) meta.quality_assurance = 'Second verification';
    else meta.quality_assurance = 'Unverified';

    // Detect schema type from XML content
    const hasProcedure = !!doc.querySelector('content procedure');
    const hasDescription = !!doc.querySelector('content description');
    const hasCrew = !!doc.querySelector('content crew');
    const detectedSchema = hasCrew ? 'crew' : hasProcedure ? 'procedural' : 'descriptive';

    // Parse crew schema
    let crewData = null;
    if (hasCrew) {
      crewData = parseCrewXml(doc);
    }

    // Parse preliminary requirements (procedural only)
    let preliminaryRqmts = null;
    if (hasProcedure) {
      preliminaryRqmts = { conditions: [], persons: [], tech_infos: [], support_equips: [], supplies: [], spares: [], safety: [] };

      // Persons — reqPersons > person | personnel
      doc.querySelectorAll('preliminaryRqmts reqPersons person, preliminaryRqmts reqPersons personnel').forEach(pe => {
        preliminaryRqmts.persons.push({
          kind: pe.tagName === 'personnel' ? 'personnel' : 'person',
          man: pe.getAttribute('man') || '',
          category: pe.querySelector('personCategory')?.getAttribute('personCategoryCode') || '',
          skill: pe.querySelector('personSkill')?.getAttribute('skillLevelCode') || '',
          trade: pe.querySelector('trade')?.textContent || '',
          time: pe.querySelector('estimatedTime')?.textContent || '',
          time_uom: pe.querySelector('estimatedTime')?.getAttribute('unitOfMeasure') || 'h',
        });
      });

      // Tech info — reqTechInfoGroup > reqTechInfo (pmRef or externalPubRef)
      doc.querySelectorAll('preliminaryRqmts reqTechInfoGroup reqTechInfo').forEach(ti => {
        const pmc = ti.querySelector('pmRef pmRefIdent pmCode');
        const ext = ti.querySelector('externalPubRef externalPubRefIdent');
        preliminaryRqmts.tech_infos.push({
          category: ti.getAttribute('reqTechInfoCategory') || '',
          ref_type: ext && !pmc ? 'ext' : 'pm',
          pm_model: pmc?.getAttribute('modelIdentCode') || '',
          pm_issuer: pmc?.getAttribute('pmIssuer') || '',
          pm_number: pmc?.getAttribute('pmNumber') || '',
          pm_volume: pmc?.getAttribute('pmVolume') || '',
          pm_title: ti.querySelector('pmRef pmRefAddressItems pmTitle')?.textContent || '',
          ext_code: ext?.querySelector('externalPubCode')?.textContent || '',
          ext_title: ext?.querySelector('externalPubTitle')?.textContent || '',
          ext_issue: ext?.querySelector('externalPubIssueInfo externalPubIssue')?.textContent || '',
        });
      });

      // Required Conditions — structure: reqCondGroup > reqCondNoRef > reqCond (text)
      //                          or:     reqCondGroup > reqCondDm > dmRef + reqCond
      const rcgEl = doc.querySelector('preliminaryRqmts reqCondGroup');
      if (rcgEl) {
        // reqCondNoRef children
        rcgEl.querySelectorAll(':scope > reqCondNoRef').forEach(nr => {
          const t = nr.querySelector('reqCond')?.textContent?.trim() || nr.textContent?.trim() || '';
          if (t) preliminaryRqmts.conditions.push({ type: 'noRef', text: t, ref: '' });
        });
        // reqCondDm children
        rcgEl.querySelectorAll(':scope > reqCondDm').forEach(dm => {
          const dmCode = dm.querySelector('dmRef dmRefIdent dmCode');
          let ref = '';
          if (dmCode) {
            ref = 'DMC-' + (dmCode.getAttribute('modelIdentCode') || '') + '-' +
              (dmCode.getAttribute('systemDiffCode') || '') + '-' +
              (dmCode.getAttribute('systemCode') || '') + '-' +
              (dmCode.getAttribute('subSystemCode') || '') +
              (dmCode.getAttribute('subSubSystemCode') || '') + '-' +
              (dmCode.getAttribute('assyCode') || '') + '-' +
              (dmCode.getAttribute('disassyCode') || '') +
              (dmCode.getAttribute('disassyCodeVariant') || '') + '-' +
              (dmCode.getAttribute('infoCode') || '') +
              (dmCode.getAttribute('infoCodeVariant') || '') + '-' +
              (dmCode.getAttribute('itemLocationCode') || '');
          }
          const t = dm.querySelector('reqCond')?.textContent?.trim() || '';
          preliminaryRqmts.conditions.push({ type: 'dmRef', text: t, ref });
        });
        // reqCondPm children
        rcgEl.querySelectorAll(':scope > reqCondPm').forEach(pm => {
          const pmCode = pm.querySelector('pmRef pmRefIdent pmCode');
          let ref = '';
          if (pmCode) {
            ref = 'PMC-' + (pmCode.getAttribute('modelIdentCode') || '') + '-' +
              (pmCode.getAttribute('pmIssuer') || '') + '-' +
              (pmCode.getAttribute('pmNumber') || '') + '-' +
              (pmCode.getAttribute('pmVolume') || '');
          }
          const t = pm.querySelector('reqCond')?.textContent?.trim() || '';
          preliminaryRqmts.conditions.push({ type: 'pmRef', text: t, ref });
        });
      }

      // Safety — reqSafety > safetyRqmts > (warning|caution|note)
      const safetyRqmtsEl = doc.querySelector('preliminaryRqmts reqSafety safetyRqmts');
      if (safetyRqmtsEl) {
        for (const child of safetyRqmtsEl.children) {
          if (child.tagName === 'warning') {
            const wp = child.querySelector('warningAndCautionPara');
            if (wp) preliminaryRqmts.safety.push({ type: 'warning', text: wp.textContent?.trim() || '' });
          } else if (child.tagName === 'caution') {
            const cp = child.querySelector('warningAndCautionPara');
            if (cp) preliminaryRqmts.safety.push({ type: 'caution', text: cp.textContent?.trim() || '' });
          } else if (child.tagName === 'note') {
            const np = child.querySelector('notePara');
            if (np) preliminaryRqmts.safety.push({ type: 'note', text: np.textContent?.trim() || '' });
          }
        }
      }

      // Supplies
      doc.querySelectorAll('preliminaryRqmts reqSupplies supplyDescrGroup supplyDescr').forEach(sd => {
        preliminaryRqmts.supplies.push({
          id: sd.getAttribute('id') || '',
          name: sd.querySelector('name')?.textContent || '',
          mfc: sd.querySelector('identNumber manufacturerCode')?.textContent || '',
          pno: sd.querySelector('identNumber partAndSerialNumber partNumber')?.textContent || '',
          quantity: sd.querySelector('reqQuantity')?.textContent || '',
        });
      });

      // Support equipment
      doc.querySelectorAll('preliminaryRqmts reqSupportEquips supportEquipDescrGroup supportEquipDescr').forEach(se => {
        preliminaryRqmts.support_equips.push({
          id: se.getAttribute('id') || '',
          name: se.querySelector('name')?.textContent || '',
          mfc: se.querySelector('identNumber manufacturerCode')?.textContent || '',
          pno: se.querySelector('identNumber partAndSerialNumber partNumber')?.textContent || '',
          quantity: se.querySelector('reqQuantity')?.textContent || '',
        });
      });

      // Spares
      doc.querySelectorAll('preliminaryRqmts reqSpares spareDescrGroup spareDescr').forEach(sp => {
        preliminaryRqmts.spares.push({
          id: sp.getAttribute('id') || '',
          name: sp.querySelector('name')?.textContent || '',
          mfc: sp.querySelector('identNumber manufacturerCode')?.textContent || '',
          pno: sp.querySelector('identNumber partAndSerialNumber partNumber')?.textContent || '',
          quantity: sp.querySelector('reqQuantity')?.textContent || '',
        });
      });
    }

    // Parse close requirements
    let closeRqmts = null;
    if (hasProcedure) {
      closeRqmts = { conditions: [] };
      const crcg = doc.querySelector('closeRqmts reqCondGroup');
      if (crcg) {
        crcg.querySelectorAll(':scope > reqCondNoRef').forEach(nr => {
          const t = nr.querySelector('reqCond')?.textContent?.trim() || nr.textContent?.trim() || '';
          if (t) closeRqmts.conditions.push({ type: 'noRef', text: t, ref: '' });
        });
        crcg.querySelectorAll(':scope > reqCondDm').forEach(dm => {
          const dmCode = dm.querySelector('dmRef dmRefIdent dmCode');
          let ref = '';
          if (dmCode) {
            ref = 'DMC-' + (dmCode.getAttribute('modelIdentCode') || '') + '-' +
              (dmCode.getAttribute('systemDiffCode') || '') + '-' +
              (dmCode.getAttribute('systemCode') || '') + '-' +
              (dmCode.getAttribute('subSystemCode') || '') +
              (dmCode.getAttribute('subSubSystemCode') || '') + '-' +
              (dmCode.getAttribute('assyCode') || '') + '-' +
              (dmCode.getAttribute('disassyCode') || '') +
              (dmCode.getAttribute('disassyCodeVariant') || '') + '-' +
              (dmCode.getAttribute('infoCode') || '') +
              (dmCode.getAttribute('infoCodeVariant') || '') + '-' +
              (dmCode.getAttribute('itemLocationCode') || '');
          }
          const t = dm.querySelector('reqCond')?.textContent?.trim() || '';
          closeRqmts.conditions.push({ type: 'dmRef', text: t, ref });
        });
      }
    }

    // Parse content - description/levelledPara or procedure/mainProcedure/proceduralStep or crew/descrCrew/levelledPara
    const levelledParas = [];
    let lpElements;
    if (hasProcedure) {
      lpElements = doc.querySelectorAll('content procedure mainProcedure proceduralStep');
    } else if (hasCrew && crewData?.crewType === 'descrCrew') {
      lpElements = doc.querySelectorAll('content crew descrCrew > levelledPara');
    } else if (hasCrew) {
      // crewRefCard — levelledParas stay empty, data is in crewData.crewDrills
      lpElements = [];
    } else {
      lpElements = doc.querySelectorAll('content description levelledPara');
    }

    const parseLpEl = (lpEl) => {
      const lp = { id: lpEl.getAttribute('id') || '', title: '', elements: [] };
      const titleNode = lpEl.querySelector(':scope > title');
      if (titleNode) lp.title = titleNode.textContent || '';

      // Process direct children in order
      for (const child of lpEl.children) {
        if (child.tagName === 'title') continue; // already handled

        if (child.tagName === 'para') {
          // Check if para contains a nested randomList or sequentialList
          const nestedRandom = child.querySelector(':scope > randomList');
          const nestedSequential = child.querySelector(':scope > sequentialList');
          const nestedList = nestedRandom || nestedSequential;

          // Build full text content walking ALL child nodes (text + inline refs)
          let paraText = '';
          const inlineRefs = [];
          for (const node of child.childNodes) {
            if (node.nodeType === 3) { // TEXT_NODE
              paraText += node.textContent;
            } else if (node.nodeType === 1) { // ELEMENT_NODE
              if (node.tagName === 'dmRef') {
                const techName = node.querySelector('dmTitle techName')?.textContent || '';
                const infoName = node.querySelector('dmTitle infoName')?.textContent || '';
                const dmCodeEl = node.querySelector('dmRefIdent dmCode');
                let refStr = '';
                if (dmCodeEl) {
                  refStr = 'DMC-' + (dmCodeEl.getAttribute('modelIdentCode') || '') + '-' +
                    (dmCodeEl.getAttribute('systemDiffCode') || '') + '-' +
                    (dmCodeEl.getAttribute('systemCode') || '') + '-' +
                    (dmCodeEl.getAttribute('subSystemCode') || '') +
                    (dmCodeEl.getAttribute('subSubSystemCode') || '') + '-' +
                    (dmCodeEl.getAttribute('assyCode') || '') + '-' +
                    (dmCodeEl.getAttribute('disassyCode') || '') +
                    (dmCodeEl.getAttribute('disassyCodeVariant') || '') + '-' +
                    (dmCodeEl.getAttribute('infoCode') || '') +
                    (dmCodeEl.getAttribute('infoCodeVariant') || '') + '-' +
                    (dmCodeEl.getAttribute('itemLocationCode') || '');
                }
                const display = techName && infoName ? `${techName} - ${infoName}` : refStr;
                paraText += `[DMRef: ${display}]`;
                inlineRefs.push({ type: 'dmRef', ref: refStr, techName, infoName, position: paraText.length });
              } else if (node.tagName === 'internalRef') {
                const refId = node.getAttribute('internalRefId') || '';
                const targetType = node.getAttribute('internalRefTargetType') || '';
                paraText += `[Ref: ${refId}]`;
                inlineRefs.push({ type: 'internalRef', refId, targetType, position: paraText.length });
              } else if (node.tagName === 'emphasis') {
                const emphType = node.getAttribute('emphasisType') || 'em01';
                const emphText = node.textContent || '';
                if (emphType === 'em01') paraText += `**${emphText}**`;
                else if (emphType === 'em02') paraText += `*${emphText}*`;
                else if (emphType === 'em03') paraText += `__${emphText}__`;
                else paraText += emphText;
                inlineRefs.push({ type: 'emphasis', emphasisType: emphType, text: emphText });
              } else if (node.tagName === 'pmRef') {
                const pmTechName = node.querySelector('pmTitle')?.textContent || '';
                const pmCodeEl = node.querySelector('pmRefIdent pmCode');
                let pmRefStr = '';
                if (pmCodeEl) {
                  pmRefStr = 'PMC-' + (pmCodeEl.getAttribute('modelIdentCode') || '') + '-' +
                    (pmCodeEl.getAttribute('pmIssuer') || '') + '-' +
                    (pmCodeEl.getAttribute('pmNumber') || '') + '-' +
                    (pmCodeEl.getAttribute('pmVolume') || '');
                }
                const pmDisplay = pmTechName || pmRefStr;
                paraText += `[PMRef: ${pmDisplay}]`;
                inlineRefs.push({ type: 'pmRef', ref: pmRefStr, title: pmTechName });
              } else if (node.tagName === 'externalPubRef') {
                const extTitle = node.querySelector('externalPubRefIdent externalPubTitle')?.textContent || '';
                const extCode = node.querySelector('externalPubRefIdent externalPubCode')?.textContent || '';
                const display = extTitle || extCode || 'External Ref';
                paraText += `[ExtRef: ${display}]`;
                inlineRefs.push({ type: 'externalPubRef', title: extTitle, code: extCode });
              } else if (node.tagName === 'randomList' || node.tagName === 'sequentialList' || node.tagName === 'definitionList') {
                // handled below via nestedList / defList
              } else {
                // Any other inline element — include its text
                paraText += node.textContent || '';
              }
            }
          }
          paraText = paraText.trim();
          const paraDefListEl = child.querySelector(':scope > definitionList');
          const paraDefList = paraDefListEl ? parseDefinitionListEl(paraDefListEl) : undefined;

          if (nestedList) {
            const listType = nestedRandom ? 'randomList' : 'sequentialList';
            const items = [];
            nestedList.querySelectorAll(':scope > listItem > para').forEach(li => {
              items.push(li.textContent || '');
            });
            lp.elements.push({
              id: uid(), type: 'para', content: paraText,
              inlineList: { type: listType, items },
              definitionList: paraDefList,
              inlineRefs: inlineRefs.length > 0 ? inlineRefs : undefined,
            });
          } else {
            lp.elements.push({
              id: uid(), type: 'para', content: paraText,
              definitionList: paraDefList,
              inlineRefs: inlineRefs.length > 0 ? inlineRefs : undefined,
            });
          }
        } else if (child.tagName === 'randomList') {
          const items = [];
          child.querySelectorAll(':scope > listItem > para').forEach(li => items.push(li.textContent || ''));
          if (items.length > 0) lp.elements.push({ id: uid(), type: 'randomList', items });
        } else if (child.tagName === 'sequentialList') {
          const items = [];
          child.querySelectorAll(':scope > listItem > para').forEach(li => items.push(li.textContent || ''));
          if (items.length > 0) lp.elements.push({ id: uid(), type: 'sequentialList', items });
        } else if (child.tagName === 'note') {
          const paras = Array.from(child.querySelectorAll(':scope > notePara')).map(n => directText(n)).filter(Boolean);
          const attnLists = [];
          Array.from(child.children).forEach(nc => {
            if (nc.tagName === 'attentionRandomList') {
              attnLists.push({ id: uid(), kind: 'random', items: parseAttnRandomItems(nc) });
            } else if (nc.tagName === 'attentionSequentialList') {
              const items = Array.from(nc.querySelectorAll(':scope > attentionSequentialListItem')).map(sli => {
                const rl = sli.querySelector(':scope > attentionRandomList');
                return {
                  text: sli.querySelector(':scope > attentionListItemPara')?.textContent?.trim() || '',
                  random: rl ? parseAttnRandomItems(rl) : undefined,
                };
              });
              attnLists.push({ id: uid(), kind: 'sequential', items });
            }
          });
          if (paras.length > 0 || attnLists.length > 0) {
            lp.elements.push({ id: uid(), type: 'note', content: paras.join('\n'), attnLists: attnLists.length > 0 ? attnLists : undefined });
          }
        } else if (child.tagName === 'warning' || child.tagName === 'caution') {
          const wps = Array.from(child.querySelectorAll(':scope > warningAndCautionPara'));
          if (wps.length > 0) {
            const paras = wps.map(w => directText(w)).filter(Boolean);
            let attnRandom;
            for (const w of wps) {
              const rl = w.querySelector(':scope > attentionRandomList');
              if (rl) { attnRandom = parseAttnRandomItems(rl); break; }
            }
            lp.elements.push({ id: uid(), type: child.tagName, content: paras.join('\n'), attnRandom });
          }
        } else if (child.tagName === 'figure') {
          const figTitle = child.querySelector(':scope > title');
          const graphics = Array.from(child.querySelectorAll(':scope > graphic'));
          const graphic = graphics[0] || null;
          const hotspots = graphic ? Array.from(graphic.querySelectorAll(':scope > hotspot')).map(parseHotspotEl) : [];
          const extraIcns = graphics.slice(1).map(g => g.getAttribute('infoEntityIdent') || '').filter(Boolean);
          const legendDl = child.querySelector(':scope > legend > definitionList');
          lp.elements.push({
            id: child.getAttribute('id') || uid(),
            type: 'figure',
            title: figTitle?.textContent || '',
            icn: graphic?.getAttribute('infoEntityIdent') || '',
            icns: extraIcns.length > 0 ? extraIcns : undefined,
            definitionList: legendDl ? parseDefinitionListEl(legendDl) : undefined,
            hotspots: hotspots.length > 0 ? hotspots : undefined,
          });
        } else if (child.tagName === 'table') {
          lp.elements.push(parseCalsTable(child));
        } else if (child.tagName === 'captionGroup') {
          lp.elements.push(parseCaptionGroup(child));
        } else if (child.tagName === 'levelledPara') {
          // nested levelledPara (descript/crew, max 4 levels)
          const nestedLp = parseLpEl(child);
          lp.elements.push({ id: nestedLp.id || uid(), type: 'levelledPara', lp: nestedLp });
        } else if (child.tagName === 'crewDrill') {
          // descrCrew levelledPara supports crewDrill (Chap 3.9.5.2.6 Para 2.3.2)
          lp.elements.push({ id: uid(), type: 'crewDrill', drill: parseCrewDrill(child) });
        } else if (child.tagName === 'proceduralStep') {
          // Nested procedural step — treat as sub-step by parsing recursively into elements
          const subPara = child.querySelector(':scope > para');
          if (subPara) {
            lp.elements.push({ id: uid(), type: 'para', content: subPara.textContent?.trim() || '' });
          }
        } else if (child.tagName === 'internalRef') {
          // internalRef inside a proceduralStep (outside of para) - skip or add as note
          // This is edge case from the sample XML
        }
      }

      return lp;
    };

    lpElements.forEach(lpEl => {
      const lp = parseLpEl(lpEl);
      if (lp.elements.length > 0 || lp.title) levelledParas.push(lp);
    });

    return { meta, levelledParas: normalizeLpIds(levelledParas, hasProcedure ? 'stp' : 'par'), rawXml: text, detectedSchema, preliminaryRqmts, closeRqmts, crewData, pmEntries: [], fileIds: Array.from(doc.querySelectorAll('[id]')).map(el => el.getAttribute('id')).filter(Boolean) };
  } catch (err) {
    console.error('Failed to parse XML file:', err);
    return null;
  }
}

/* ═══════════════════════════════════════════
   REQUIRED CONDITIONS GROUP EDITOR
   Shared by Preliminary and Close Requirements.
   Three condition types: NoRef, DM Ref, PM Ref.
   ═══════════════════════════════════════════ */
const COND_TYPES = [
  { value: 'noRef', label: 'No Reference', badge: 'NoRef', color: 'bg-slate-100 text-slate-700' },
  { value: 'dmRef', label: 'DM Reference', badge: 'DM', color: 'bg-violet-100 text-violet-700' },
  { value: 'pmRef', label: 'PM Reference', badge: 'PM', color: 'bg-blue-100 text-blue-700' },
];

const ReqCondGroupEditor = ({ conditions, onChange, dirFiles, currentDmc, dmlEntries = [] }) => {
  const [addType, setAddType] = useState(null);

  const dmFiles = (dirFiles || []).filter(f => {
    const n = f.name.toUpperCase();
    return n.startsWith('DMC-') && n.endsWith('.XML');
  });
  const pmFiles = (dirFiles || []).filter(f => {
    const n = f.name.toUpperCase();
    return n.startsWith('PMC-') && n.endsWith('.XML');
  });
  const currentDmcUpper = (currentDmc || '').toUpperCase();

  // Combine DML Builder entries + directory files for dmRef dropdown (deduped)
  const dmOptions = (() => {
    const seen = new Set();
    const opts = [];
    for (const e of dmlEntries) {
      const dmc = e.dmc_formatted;
      if (dmc && !seen.has(dmc.toUpperCase())) {
        seen.add(dmc.toUpperCase());
        const isCurrent = dmc.toUpperCase() === currentDmcUpper;
        opts.push({ value: dmc, label: `${dmc} — ${e.title || ''}`, disabled: isCurrent });
      }
    }
    for (const f of dmFiles) {
      const dmcName = f.name.replace(/\.xml$/i, '');
      if (!seen.has(dmcName.toUpperCase())) {
        seen.add(dmcName.toUpperCase());
        const isCurrent = dmcName.toUpperCase() === currentDmcUpper || f.name.toUpperCase().startsWith(currentDmcUpper);
        opts.push({ value: dmcName, label: dmcName, disabled: isCurrent });
      }
    }
    return opts;
  })();

  const addCondition = (type) => {
    onChange([...(conditions || []), { type, text: '', ref: '' }]);
    setAddType(null);
  };

  const updateCond = (idx, field, value) => {
    const arr = [...(conditions || [])];
    arr[idx] = { ...arr[idx], [field]: value };
    onChange(arr);
  };

  const removeCond = (idx) => {
    onChange((conditions || []).filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Required Conditions</span>
        <div className="flex items-center gap-1">
          {addType === null ? (
            <Button variant="ghost" size="sm" className="text-xs h-6 gap-1 text-teal-600"
              onClick={() => setAddType('pick')} data-testid="add-condition-btn">
              <Plus className="w-3 h-3" /> Add Condition
            </Button>
          ) : (
            <div className="flex items-center gap-1 bg-slate-50 rounded-md px-2 py-1 border border-slate-200">
              {COND_TYPES.map(ct => (
                <Button key={ct.value} variant="ghost" size="sm"
                  className="text-xs h-6 px-2"
                  onClick={() => addCondition(ct.value)}
                  data-testid={`add-cond-${ct.value}`}>
                  {ct.label}
                </Button>
              ))}
              <button onClick={() => setAddType(null)} className="ml-1 text-slate-400 hover:text-slate-600">
                <XIcon className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      </div>

      {(!conditions || conditions.length === 0) ? (
        <p className="text-xs text-slate-400 italic ml-2">None (noConds)</p>
      ) : (
        <div className="space-y-2">
          {conditions.map((c, i) => {
            const typeInfo = COND_TYPES.find(t => t.value === c.type) || COND_TYPES[0];
            return (
              <div key={i} className="border border-slate-200 rounded-lg p-2.5 bg-white group hover:border-slate-300 transition-colors" data-testid={`condition-row-${i}`}>
                <div className="flex items-start gap-2">
                  <Badge className={`text-[10px] shrink-0 mt-0.5 ${typeInfo.color}`}>{typeInfo.badge}</Badge>
                  <div className="flex-1 space-y-1.5">
                    <Input
                      value={c.text || ''}
                      onChange={e => updateCond(i, 'text', e.target.value)}
                      placeholder="Condition text..."
                      className="text-xs h-7"
                      data-testid={`condition-text-${i}`}
                    />
                    {c.type === 'dmRef' && (
                      <Select value={c.ref || '_none'} onValueChange={v => updateCond(i, 'ref', v === '_none' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs" data-testid={`condition-dm-select-${i}`}>
                          <SelectValue placeholder="Select Data Module..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">Select Data Module...</SelectItem>
                          {dmOptions.length > 0 ? dmOptions.map((opt, oi) => (
                            <SelectItem key={oi} value={opt.value} disabled={opt.disabled}>
                              {opt.label}{opt.disabled ? ' (current)' : ''}
                            </SelectItem>
                          )) : (
                            <SelectItem value="_empty" disabled>No DML entries or directory files</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    )}
                    {c.type === 'pmRef' && (
                      <Select value={c.ref || '_none'} onValueChange={v => updateCond(i, 'ref', v === '_none' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs" data-testid={`condition-pm-select-${i}`}>
                          <SelectValue placeholder="Select Publication Module..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">Select Publication Module...</SelectItem>
                          {pmFiles.map(f => {
                            const pmcName = f.name.replace(/\.xml$/i, '');
                            return (
                              <SelectItem key={f.name} value={pmcName}>{pmcName}</SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  <button onClick={() => removeCond(i)}
                    className="p-1 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 shrink-0">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


/* ═══════════════════════════════════════════
   PRELIMINARY REQUIREMENTS EDITOR (Procedural)
   ═══════════════════════════════════════════ */
const ReqSectionRow = ({ label, section, fields, template, items, addItem, updateField, removeItem }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{label}</span>
      <Button variant="ghost" size="sm" className="text-xs h-6 gap-1 text-teal-600" onClick={() => addItem(section, template)} data-testid={`add-${section}-btn`}>
        <Plus className="w-3 h-3" /> Add
      </Button>
    </div>
    {(items || []).length === 0 ? (
      <p className="text-xs text-slate-400 italic ml-2">None</p>
    ) : (
      (items || []).map((item, i) => (
        <div key={i} className="flex items-center gap-2 group">
          {fields.map(f => (
            <Input
              key={f.key}
              value={item[f.key] || ''}
              onChange={e => updateField(section, i, f.key, e.target.value)}
              placeholder={f.placeholder}
              className={`text-xs h-7 ${f.narrow ? 'w-16' : 'flex-1'}`}
              data-testid={`${section}-${f.key}-${i}`}
            />
          ))}
          <button onClick={() => removeItem(section, i)} className="p-1 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      ))
    )}
  </div>
);

const ReqPersonsSection = ({ items, addItem, updateField, removeItem }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Personnel (reqPersons)</span>
      <Button variant="ghost" size="sm" className="text-xs h-6 gap-1 text-teal-600" onClick={() => addItem('persons', { kind: 'person', man: '', category: '', skill: '', trade: '', time: '', time_uom: 'h' })} data-testid="add-persons-btn">
        <Plus className="w-3 h-3" /> Add
      </Button>
    </div>
    {(items || []).length === 0 ? (
      <p className="text-xs text-slate-400 italic ml-2">None</p>
    ) : (
      (items || []).map((p, i) => (
        <div key={i} className="flex items-center gap-2 group">
          <Select value={p.kind || 'person'} onValueChange={v => updateField('persons', i, 'kind', v)}>
            <SelectTrigger className="text-xs h-7 w-28" data-testid={`persons-kind-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="person">person</SelectItem>
              <SelectItem value="personnel">personnel</SelectItem>
            </SelectContent>
          </Select>
          {p.kind !== 'personnel' && (
            <Input value={p.man || ''} onChange={e => updateField('persons', i, 'man', e.target.value)} placeholder="Man" className="text-xs h-7 w-14" data-testid={`persons-man-${i}`} />
          )}
          <Input value={p.category || ''} onChange={e => updateField('persons', i, 'category', e.target.value)} placeholder="Category (e.g. Chemical technician)" className="text-xs h-7 flex-1" data-testid={`persons-category-${i}`} />
          <Input value={p.skill || ''} onChange={e => updateField('persons', i, 'skill', e.target.value)} placeholder="sk02" className="text-xs h-7 w-16 font-mono" data-testid={`persons-skill-${i}`} />
          <Input value={p.trade || ''} onChange={e => updateField('persons', i, 'trade', e.target.value)} placeholder="Trade" className="text-xs h-7 flex-1" data-testid={`persons-trade-${i}`} />
          <Input value={p.time || ''} onChange={e => updateField('persons', i, 'time', e.target.value)} placeholder="Time" className="text-xs h-7 w-14" data-testid={`persons-time-${i}`} />
          <Input value={p.time_uom || ''} onChange={e => updateField('persons', i, 'time_uom', e.target.value)} placeholder="h" className="text-xs h-7 w-11" data-testid={`persons-uom-${i}`} />
          <button onClick={() => removeItem('persons', i)} className="p-1 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      ))
    )}
  </div>
);

const ReqTechInfoSection = ({ items, addItem, updateField, removeItem, pmFiles = [], onPickPm = null }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Technical Information (reqTechInfoGroup)</span>
      <Button variant="ghost" size="sm" className="text-xs h-6 gap-1 text-teal-600" onClick={() => addItem('tech_infos', { category: 'ti01', ref_type: 'pm', pm_model: '', pm_issuer: '', pm_number: '', pm_volume: '00', pm_title: '', ext_code: '', ext_title: '', ext_issue: '' })} data-testid="add-tech_infos-btn">
        <Plus className="w-3 h-3" /> Add
      </Button>
    </div>
    {(items || []).length === 0 ? (
      <p className="text-xs text-slate-400 italic ml-2">None</p>
    ) : (
      (items || []).map((ti, i) => (
        <div key={i} className="p-2 rounded border border-slate-100 space-y-1.5 group">
          <div className="flex items-center gap-2">
            <Input value={ti.category || ''} onChange={e => updateField('tech_infos', i, 'category', e.target.value)} placeholder="ti01" className="text-xs h-7 w-16 font-mono" data-testid={`tech_infos-category-${i}`} />
            <Select value={ti.ref_type || 'pm'} onValueChange={v => updateField('tech_infos', i, 'ref_type', v)}>
              <SelectTrigger className="text-xs h-7 w-40" data-testid={`tech_infos-reftype-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pm">Publication (pmRef)</SelectItem>
                <SelectItem value="ext">External Pub Ref</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex-1" />
            <button onClick={() => removeItem('tech_infos', i)} className="p-1 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
          {ti.ref_type === 'ext' ? (
            <div className="flex items-center gap-2">
              <Input value={ti.ext_code || ''} onChange={e => updateField('tech_infos', i, 'ext_code', e.target.value)} placeholder="Pub code" className="text-xs h-7 w-40 font-mono" data-testid={`tech_infos-extcode-${i}`} />
              <Input value={ti.ext_title || ''} onChange={e => updateField('tech_infos', i, 'ext_title', e.target.value)} placeholder="Pub title" className="text-xs h-7 flex-1" data-testid={`tech_infos-exttitle-${i}`} />
              <Input value={ti.ext_issue || ''} onChange={e => updateField('tech_infos', i, 'ext_issue', e.target.value)} placeholder="Issue" className="text-xs h-7 w-20" data-testid={`tech_infos-extissue-${i}`} />
            </div>
          ) : (
            <>
              {pmFiles.length > 0 && onPickPm && (
                <Select onValueChange={(val) => onPickPm(i, pmFiles[parseInt(val, 10)])}>
                  <SelectTrigger className="text-xs h-7 bg-teal-50/50 border-teal-200" data-testid={`tech_infos-pmpicker-${i}`}>
                    <SelectValue placeholder="Pick publication module from working directory..." />
                  </SelectTrigger>
                  <SelectContent>
                    {pmFiles.map((f, fi) => (
                      <SelectItem key={fi} value={String(fi)}>
                        <span className="font-mono text-[10px]">{f.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <div className="flex items-center gap-2">
              <Input value={ti.pm_model || ''} onChange={e => updateField('tech_infos', i, 'pm_model', e.target.value)} placeholder="Model ident" className="text-xs h-7 w-32 font-mono" data-testid={`tech_infos-pmmodel-${i}`} />
              <Input value={ti.pm_issuer || ''} onChange={e => updateField('tech_infos', i, 'pm_issuer', e.target.value)} placeholder="Issuer" className="text-xs h-7 w-20 font-mono" data-testid={`tech_infos-pmissuer-${i}`} />
              <Input value={ti.pm_number || ''} onChange={e => updateField('tech_infos', i, 'pm_number', e.target.value)} placeholder="Number" className="text-xs h-7 w-20 font-mono" data-testid={`tech_infos-pmnumber-${i}`} />
              <Input value={ti.pm_volume || ''} onChange={e => updateField('tech_infos', i, 'pm_volume', e.target.value)} placeholder="Vol" className="text-xs h-7 w-14 font-mono" data-testid={`tech_infos-pmvolume-${i}`} />
              <Input value={ti.pm_title || ''} onChange={e => updateField('tech_infos', i, 'pm_title', e.target.value)} placeholder="Publication title" className="text-xs h-7 flex-1" data-testid={`tech_infos-pmtitle-${i}`} />
            </div>
            </>
          )}
        </div>
      ))
    )}
  </div>
);

const PreliminaryRqmtsEditor = ({ data, onChange, dirFiles, currentDmc, dmlEntries = [], collapsed: collapsedProp, onToggleCollapse }) => {
  const [localCollapsed, setLocalCollapsed] = useState(false);
  const collapsed = onToggleCollapse ? !!collapsedProp : localCollapsed;
  const toggleCollapsed = () => (onToggleCollapse ? onToggleCollapse() : setLocalCollapsed(!localCollapsed));
  const d = data || { conditions: [], support_equips: [], supplies: [], spares: [], safety: [] };

  const updateField = (section, idx, field, value) => {
    const arr = [...(d[section] || [])];
    arr[idx] = { ...arr[idx], [field]: value };
    onChange({ ...d, [section]: arr });
  };
  const addItem = (section, template) => {
    // S1000D id prefixes: seq- (support equipment), sup- (supplies), spa- (spares)
    const prefix = { support_equips: 'seq', supplies: 'sup', spares: 'spa' }[section];
    let item = template;
    if (prefix) {
      let max = 0;
      const re = new RegExp(`^${prefix}-(\\d{4,})$`);
      (d[section] || []).forEach(it => { const m = re.exec(it.id || ''); if (m) max = Math.max(max, parseInt(m[1], 10)); });
      item = { ...template, id: `${prefix}-${String(max + 1).padStart(4, '0')}` };
    }
    onChange({ ...d, [section]: [...(d[section] || []), item] });
  };
  const removeItem = (section, idx) => {
    onChange({ ...d, [section]: (d[section] || []).filter((_, i) => i !== idx) });
  };

  const pmFiles = (dirFiles || []).filter(f => /^PMC-/i.test(f.name) && /\.xml$/i.test(f.name));

  const pickPmFromFile = async (idx, fileEntry) => {
    if (!fileEntry?.handle) return;
    try {
      const file = await fileEntry.handle.getFile();
      const text = await file.text();
      const doc = new DOMParser().parseFromString(text, 'application/xml');
      const pmc = doc.querySelector('pmIdent pmCode');
      const pmTitle = doc.querySelector('pmTitle')?.textContent?.trim() || '';
      onChange({
        ...d,
        tech_infos: (d.tech_infos || []).map((t, i) => i === idx ? {
          ...t,
          ref_type: 'pm',
          pm_model: pmc?.getAttribute('modelIdentCode') || t.pm_model || '',
          pm_issuer: pmc?.getAttribute('pmIssuer') || t.pm_issuer || '',
          pm_number: pmc?.getAttribute('pmNumber') || t.pm_number || '',
          pm_volume: pmc?.getAttribute('pmVolume') || t.pm_volume || '',
          pm_title: pmTitle || t.pm_title || '',
        } : t),
      });
      toast.success(`Publication details filled from ${fileEntry.name}`);
    } catch {
      toast.error('Could not read the publication module file');
    }
  };

  return (
    <Card className="border-teal-200" data-testid="prelim-rqmts-editor">
      <div className="flex items-center justify-between px-4 py-2.5 bg-teal-50 rounded-t-lg border-b border-teal-200 cursor-pointer" onClick={toggleCollapsed}>
        <div className="flex items-center gap-2 text-sm font-medium text-teal-800">
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          <Badge className="bg-teal-100 text-teal-700 text-[10px]">preliminaryRqmts</Badge>
          Preliminary Requirements
        </div>
      </div>
      {!collapsed && (
        <CardContent className="p-4 space-y-4">
          <ReqCondGroupEditor
            conditions={d.conditions || []}
            onChange={conds => onChange({ ...d, conditions: conds })}
            dirFiles={dirFiles}
            currentDmc={currentDmc}
            dmlEntries={dmlEntries}
          />
          <div className="border-t border-slate-100 pt-4" />
          <ReqPersonsSection items={d.persons} addItem={addItem} updateField={updateField} removeItem={removeItem} />
          <ReqTechInfoSection items={d.tech_infos} addItem={addItem} updateField={updateField} removeItem={removeItem} pmFiles={pmFiles} onPickPm={pickPmFromFile} />
          <ReqSectionRow
            label="Support Equipment" section="support_equips"
            fields={[{ key: 'name', placeholder: 'Equipment name' }, { key: 'mfc', placeholder: 'Mfr code' }, { key: 'pno', placeholder: 'Part number' }, { key: 'quantity', placeholder: 'Qty', narrow: true }]}
            template={{ id: '', name: '', mfc: '', pno: '', quantity: '' }}
            items={d.support_equips} addItem={addItem} updateField={updateField} removeItem={removeItem}
          />
          <ReqSectionRow
            label="Supplies" section="supplies"
            fields={[{ key: 'name', placeholder: 'Supply name' }, { key: 'mfc', placeholder: 'Mfr code' }, { key: 'pno', placeholder: 'Part number' }, { key: 'quantity', placeholder: 'Qty', narrow: true }]}
            template={{ id: '', name: '', mfc: '', pno: '', quantity: '' }}
            items={d.supplies} addItem={addItem} updateField={updateField} removeItem={removeItem}
          />
          <ReqSectionRow
            label="Spares" section="spares"
            fields={[{ key: 'name', placeholder: 'Spare name' }, { key: 'mfc', placeholder: 'Mfr code' }, { key: 'pno', placeholder: 'Part number' }, { key: 'quantity', placeholder: 'Qty', narrow: true }]}
            template={{ id: '', name: '', mfc: '', pno: '', quantity: '' }}
            items={d.spares} addItem={addItem} updateField={updateField} removeItem={removeItem}
          />
          {/* Safety Requirements — supports warning, caution, note types */}
          <div className="space-y-2" data-testid="safety-section">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Safety Requirements</span>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" className="text-xs h-6 gap-1 text-red-600" onClick={() => addItem('safety', { type: 'warning', text: '' })} data-testid="add-safety-warning">
                  <Plus className="w-3 h-3" /> Warning
                </Button>
                <Button variant="ghost" size="sm" className="text-xs h-6 gap-1 text-amber-600" onClick={() => addItem('safety', { type: 'caution', text: '' })} data-testid="add-safety-caution">
                  <Plus className="w-3 h-3" /> Caution
                </Button>
                <Button variant="ghost" size="sm" className="text-xs h-6 gap-1 text-blue-600" onClick={() => addItem('safety', { type: 'note', text: '' })} data-testid="add-safety-note">
                  <Plus className="w-3 h-3" /> Note
                </Button>
              </div>
            </div>
            {(d.safety || []).length === 0 ? (
              <p className="text-xs text-slate-400 italic ml-2">None (noSafety)</p>
            ) : (
              (d.safety || []).map((item, i) => {
                const safetyStyles = {
                  warning: { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700' },
                  caution: { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700' },
                  note: { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700' },
                };
                const s = safetyStyles[item.type] || safetyStyles.warning;
                return (
                  <div key={i} className={`${s.bg} ${s.border} border rounded-lg p-2.5 group`} data-testid={`safety-row-${i}`}>
                    <div className="flex items-start gap-2">
                      <div className="shrink-0 space-y-1">
                        <Badge className={`text-[10px] ${s.badge}`}>{(item.type || 'warning').toUpperCase()}</Badge>
                        <Select value={item.type || 'warning'} onValueChange={v => updateField('safety', i, 'type', v)}>
                          <SelectTrigger className="h-6 text-[10px] w-[90px] bg-white" data-testid={`safety-type-${i}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="warning">Warning</SelectItem>
                            <SelectItem value="caution">Caution</SelectItem>
                            <SelectItem value="note">Note</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Textarea
                        value={item.text || ''}
                        onChange={e => updateField('safety', i, 'text', e.target.value)}
                        placeholder={`${(item.type || 'warning')} text...`}
                        rows={2}
                        className="text-xs flex-1 resize-y bg-white/80 min-h-[38px]"
                        data-testid={`safety-text-${i}`}
                      />
                      <button onClick={() => removeItem('safety', i)} className="p-1 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 shrink-0">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

/* ═══════════════════════════════════════════
   CLOSE REQUIREMENTS EDITOR (Procedural)
   Same condition types as Preliminary Requirements.
   ═══════════════════════════════════════════ */
const CloseRqmtsEditor = ({ data, onChange, dirFiles, currentDmc, dmlEntries = [], collapsed: collapsedProp, onToggleCollapse }) => {
  const [localCollapsed, setLocalCollapsed] = useState(true);
  const collapsed = onToggleCollapse ? !!collapsedProp : localCollapsed;
  const toggleCollapsed = () => (onToggleCollapse ? onToggleCollapse() : setLocalCollapsed(!localCollapsed));
  const d = data || { conditions: [] };

  return (
    <Card className="border-teal-200" data-testid="close-rqmts-editor">
      <div className="flex items-center justify-between px-4 py-2.5 bg-teal-50 rounded-t-lg border-b border-teal-200 cursor-pointer" onClick={toggleCollapsed}>
        <div className="flex items-center gap-2 text-sm font-medium text-teal-800">
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          <Badge className="bg-teal-100 text-teal-700 text-[10px]">closeRqmts</Badge>
          Closing Requirements
        </div>
      </div>
      {!collapsed && (
        <CardContent className="p-4">
          <ReqCondGroupEditor
            conditions={d.conditions || []}
            onChange={conds => onChange({ ...d, conditions: conds })}
            dirFiles={dirFiles}
            currentDmc={currentDmc}
            dmlEntries={dmlEntries}
          />
        </CardContent>
      )}
    </Card>
  );
};

/* ═══════════════════════════════════════════
   MAIN AUTHOR PAGE
   ═══════════════════════════════════════════ */
const AuthorPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const projects = useProjects();
  const projectState = location.state?.project;
  const initialProjectId = location.state?.projectId || projectState?.id;
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Project
  const [selectedProject, setSelectedProject] = useState(initialProjectId || 'none');
  const currentProject = projects.find(p => p.id === selectedProject);
  const projectId = selectedProject !== 'none' ? selectedProject : null;

  // DML data
  const [dmlEntries, setDmlEntries] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);

  // Working directory
  const [dirHandle, setDirHandle] = useState(null);
  const [dirFiles, setDirFiles] = useState([]);
  const [dirIcns, setDirIcns] = useState([]);
  const [dirName, setDirName] = useState('');
  const [scanningDir, setScanningDir] = useState(false);

  // View: 'list' or 'editor'
  const [view, setView] = useState('list');

  // Editor state
  const [editingEntry, setEditingEntry] = useState(null);
  const [dmc, setDmc] = useState('');
  const [titleField, setTitleField] = useState('');
  const [schemaType, setSchemaType] = useState('descriptive');
  const [security, setSecurity] = useState('01');
  const [rpc, setRpc] = useState('');
  const [issueNumber, setIssueNumber] = useState('001');
  const [inWork, setInWork] = useState('00');
  const [qa, setQa] = useState('Unverified');
  const [levelledParas, setLevelledParas] = useState([]);
  const [preliminaryRqmts, setPreliminaryRqmts] = useState(null);
  const [closeRqmts, setCloseRqmts] = useState(null);
  const [crewType, setCrewType] = useState('crewRefCard');
  const [crewTitle, setCrewTitle] = useState('');
  const [crewDrills, setCrewDrills] = useState([]);
  const [collapsedSections, setCollapsedSections] = useState({ close: true });
  const toggleSection = (key) => setCollapsedSections((p) => ({ ...p, [key]: !p[key] }));
  const [activeSection, setActiveSection] = useState(null);
  const [fileIds, setFileIds] = useState([]);

  // ── Internal reference validation: refs pointing to missing ids ──
  const brokenRefs = useMemo(() => {
    const ids = new Set(fileIds);
    levelledParas.forEach(lp => {
      if (lp.id) ids.add(lp.id);
      (lp.elements || []).forEach(el => {
        if (el.id) ids.add(el.id);
        (el.hotspots || []).forEach(h => { if (h.id) ids.add(h.id); });
      });
    });
    const broken = [];
    levelledParas.forEach((lp, i) => {
      (lp.elements || []).forEach(el => {
        (el.inlineRefs || []).forEach(r => {
          if (r.type === 'internalRef' && r.refId && !ids.has(r.refId)) {
            broken.push({ refId: r.refId, section: lp.title || `${schemaType === 'procedural' ? 'Step' : 'Para'} ${i + 1}` });
          }
        });
      });
    });
    return broken;
  }, [levelledParas, schemaType, fileIds]);

  // ── Internal reference targets (figures, tables, paras/steps with ids in this DM) ──
  const irttOptions = useMemo(() => getInternalRefTypes(currentProject?.s1000d_version), [currentProject]);
  const internalTargets = useMemo(() => {
    const is40 = /4\.0/.test(currentProject?.s1000d_issue || '');
    const tt = (v41, v40) => (is40 ? v40 : v41);
    const targets = [];
    levelledParas.forEach((lp, i) => {
      if (lp.id) {
        targets.push({
          id: lp.id,
          targetType: schemaType === 'procedural' ? tt('irtt08', 'step') : tt('irtt07', 'para'),
          label: `${schemaType === 'procedural' ? 'Step' : 'Para'} ${i + 1}${lp.title ? ` — ${lp.title}` : ''}`,
        });
      }
      (lp.elements || []).forEach((el) => {
        if (el.type === 'crewDrill' && el.drill) {
          collectCrewTargets([el.drill], targets, tt);
        }
        if (!el.id) return;
        if (el.type === 'figure') {
          targets.push({ id: el.id, targetType: tt('irtt01', 'figure'), label: `Figure — ${el.title || el.id}` });
          (el.hotspots || []).forEach(h => {
            if (h.id) targets.push({ id: h.id, targetType: tt('irtt11', 'hotspot'), label: `Hotspot — ${h.title || h.id}` });
          });
        } else if (el.type === 'table' || el.type === 'captionGroup') {
          targets.push({ id: el.id, targetType: tt('irtt02', 'table'), label: `Table — ${el.title || el.id}` });
        }
      });
    });
    if (schemaType === 'crew') collectCrewTargets(crewDrills, targets, tt);
    return targets;
  }, [levelledParas, schemaType, currentProject, crewDrills]);
  const [pmEntries, setPmEntries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState('');
  const [fileXmlContent, setFileXmlContent] = useState(null); // raw XML loaded from working dir

  // Track unsaved changes
  useEffect(() => {
    const current = JSON.stringify({ dmc, titleField, schemaType, security, rpc, issueNumber, inWork, qa, levelledParas, crewType, crewTitle, crewDrills, pmEntries });
    setHasUnsaved(current !== savedSnapshot);
  }, [dmc, titleField, schemaType, security, rpc, issueNumber, inWork, qa, levelledParas, crewType, crewTitle, crewDrills, pmEntries, savedSnapshot]);

  // ── Fetch DML entries + authored modules ──
  const fetchData = useCallback(async () => {
    if (!projectId) { setDmlEntries([]); setModules([]); return; }
    setLoading(true);
    try {
      const dmlRes = await axios.get(`${API}/dml?project_id=${projectId}`, { headers });
      const dmls = dmlRes.data || [];
      const entries = [];
      for (const dml of dmls) {
        try {
          const detail = await axios.get(`${API}/dml/${dml.id}`, { headers });
          (detail.data?.entries || []).forEach(entry => {
            entries.push({
              dmc_formatted: entry.dmc_formatted || '',
              title: entry.title || '',
              schema: entry.schema || '',
              dml_id: dml.id,
              dmc: entry.dmc || {},
              pm_code: entry.pm_code || '',
              ddn_code: entry.ddn_code || '',
              entry_type: entry.entry_type || 'dm',
            });
          });
        } catch { /* skip */ }
      }
      setDmlEntries(entries);

      const authRes = await axios.get(`${API}/author/modules/${projectId}`, { headers });
      setModules(authRes.data || []);
    } catch { toast.error('Failed to load data'); }
    setLoading(false);
  }, [projectId, headers]);

  useEffect(() => {
    // Clear any previous project's data immediately on switch, then load
    setDmlEntries([]);
    setModules([]);
    fetchData();
  }, [fetchData]);

  // ── Working directory ──
  const scanDirectory = useCallback(async (handle) => {
    if (!handle) return;
    setScanningDir(true);
    try {
      const files = [];
      const icns = [];
      const IMG_EXT = /\.(png|jpe?g|gif|tiff?|svg|cgm|bmp|webp)$/i;
      for await (const { entry, path } of walkDirectory(handle)) {
        if (IMG_EXT.test(entry.name)) {
          icns.push({ name: entry.name, base: entry.name.replace(IMG_EXT, ''), handle: entry, path });
        }
        if (entry.name.toLowerCase().endsWith('.xml')) {
          // Read first 2KB to detect schema from xsi:noNamespaceSchemaLocation
          let detectedSchema = null;
          try {
            const fileObj = await entry.getFile();
            const snippet = await fileObj.slice(0, 2048).text();
            detectedSchema = detectSchemaFromXml(snippet);
          } catch { /* ignore read errors */ }
          files.push({ name: entry.name, handle: entry, detectedSchema, path });
        }
      }
      files.sort((a, b) => a.name.localeCompare(b.name));
      icns.sort((a, b) => a.name.localeCompare(b.name));
      setDirFiles(files);
      setDirIcns(icns);
    } catch { setDirFiles([]); setDirIcns([]); }
    setScanningDir(false);
  }, []);

  // Load the working directory linked to the SELECTED PROJECT (Project Settings store)
  useEffect(() => {
    (async () => {
      setDirHandle(null); setDirFiles([]); setDirIcns([]); setDirName('');
      if (!projectId) return;
      try {
        const record = await getProjectDirHandle(projectId);
        const saved = record?.handle;
        if (saved) {
          setDirHandle(saved);
          setDirName(record.displayName || saved.name);
          const perm = await saved.queryPermission({ mode: 'read' });
          if (perm === 'granted') await scanDirectory(saved);
        }
      } catch { /* no saved handle for this project */ }
    })();
  }, [projectId, scanDirectory]);

  const linkDirectory = async () => {
    if (!window.showDirectoryPicker) {
      toast.error('Your browser does not support the File System Access API. Use Chrome or Edge.');
      return;
    }
    try {
      const handle = await window.showDirectoryPicker({ mode: 'read' });
      setDirHandle(handle);
      setDirName(handle.name);
      await scanDirectory(handle);
      if (projectId) {
        try {
          await saveProjectDirHandle(projectId, handle, handle.name);
          await axios.put(`${API}/projects/${projectId}`, { working_directory: handle.name }, { headers: { Authorization: `Bearer ${token}` } });
        } catch { /* non-critical */ }
      }
      toast.success(`Linked to "${handle.name}"`);
    } catch (err) {
      if (err.name !== 'AbortError') toast.error('Failed to access directory');
    }
  };

  const refreshDir = async () => {
    if (!dirHandle) return;
    try {
      let perm = await dirHandle.queryPermission({ mode: 'read' });
      if (perm !== 'granted') {
        perm = await dirHandle.requestPermission({ mode: 'read' });
        if (perm !== 'granted') { toast.error('Permission denied'); return; }
      }
      await scanDirectory(dirHandle);
      toast.success('Directory refreshed');
    } catch {
      toast.error('Directory access lost. Please re-link.');
      setDirHandle(null); setDirFiles([]); setDirIcns([]); setDirName('');
    }
  };

  const unlinkDirectory = async () => {
    setDirHandle(null); setDirFiles([]); setDirIcns([]); setDirName('');
    if (projectId) {
      try {
        await removeProjectDirHandle(projectId);
        await axios.put(`${API}/projects/${projectId}`, { working_directory: '' }, { headers: { Authorization: `Bearer ${token}` } });
      } catch { /* non-critical */ }
    }
    toast.info('Working directory unlinked');
  };

  // ── Match DML entries with working dir files and authored modules ──
  const { enrichedEntries, unmatchedDirFiles } = useMemo(() => {
    const matchedFileNames = new Set();

    const entries = dmlEntries.map((entry, idx) => {
      const authored = modules.find(m => m.dmc === entry.dmc_formatted);
      // Try to match file by DMC prefix
      let fileMatch = null;
      const dmcUpper = (entry.dmc_formatted || '').toUpperCase();
      const pmUpper = (entry.pm_code || '').toUpperCase();
      const ddnUpper = (entry.ddn_code || '').toUpperCase();
      for (const f of dirFiles) {
        const fname = f.name.toUpperCase();
        if ((dmcUpper && fname.startsWith(dmcUpper)) || (pmUpper && fname.startsWith(pmUpper)) || (ddnUpper && fname.startsWith(ddnUpper))) {
          if (!fileMatch) fileMatch = f;
          matchedFileNames.add(f.name);
        }
      }
      // Use file's detected schema if available, else DML entry schema
      const detectedSchema = fileMatch?.detectedSchema || entry.schema || null;
      const displayCode = (entry.entry_type === 'pm' && entry.pm_code) ? entry.pm_code
        : (entry.entry_type === 'ddn' && entry.ddn_code) ? entry.ddn_code
        : entry.dmc_formatted;
      return { ...entry, idx, authored, fileMatch, source: 'dml', detectedSchema, displayCode };
    });

    // Files in the working directory that don't match any DML entry
    const unmatched = dirFiles
      .filter(f => !matchedFileNames.has(f.name))
      .map((f, i) => {
        const base = f.name.replace(/\.xml$/i, '');
        return {
          dmc_formatted: base,
          displayCode: base,
          title: f.name,
          schema: f.detectedSchema || '',
          dml_id: null,
          dmc: {},
          idx: dmlEntries.length + i,
          authored: modules.find(m => m.dmc === base) || null,
          fileMatch: f,
          source: 'directory',
          detectedSchema: f.detectedSchema || null,
        };
      });

    return { enrichedEntries: entries, unmatchedDirFiles: unmatched };
  }, [dmlEntries, modules, dirFiles]);

  // ── Search filter ──
  const [search, setSearch] = useState('');
  const { visibleEntries, visibleDirFiles } = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return { visibleEntries: enrichedEntries, visibleDirFiles: unmatchedDirFiles };
    const match = (e) =>
      (e.dmc_formatted || '').toLowerCase().includes(q) ||
      (e.displayCode || '').toLowerCase().includes(q) ||
      (e.title || '').toLowerCase().includes(q) ||
      ((e.detectedSchema || e.schema || '')).toLowerCase().includes(q) ||
      (e.fileMatch?.name || '').toLowerCase().includes(q);
    return { visibleEntries: enrichedEntries.filter(match), visibleDirFiles: unmatchedDirFiles.filter(match) };
  }, [search, enrichedEntries, unmatchedDirFiles]);

  // ── Add directory file to DML ──
  const [addingToDml, setAddingToDml] = useState(null);

  const ensureDml = async () => {
    const res = await axios.get(`${API}/dml?project_id=${projectId}`, { headers });
    if (res.data.length > 0) return res.data[0].id;
    const mic = currentProject?.model_ident_codes?.[0] || 'PROJ';
    const createResp = await axios.post(`${API}/dml`, {
      project_id: projectId,
      dml_code: `DML-${mic}-${projectId.substring(0, 5).toUpperCase()}`,
      title: `${currentProject?.title || 'Project'} DML`,
      model_ident_code: mic,
      dml_type: 'S', issue_number: '001', in_work: '00',
      security_classification: currentProject?.security_classification || '01 - Unclassified',
      year_of_issue: String(new Date().getFullYear()),
      sequence_number: '00001', sender: '', sender_cage: '', entries: [],
    }, { headers });
    return createResp.data.id;
  };

  const addFileToDml = async (entry) => {
    if (!projectId || !entry.fileMatch) return;
    setAddingToDml(entry.fileMatch.name);
    try {
      const { payload, error } = await buildEntryPayload(entry);
      if (!payload) {
        toast.error(`${entry.fileMatch.name}: ${error}`);
        return;
      }
      const dmlId = await ensureDml();
      await axios.post(`${API}/dml/${dmlId}/entry`, payload, { headers });
      toast.success(`Added "${payload.title}" to DML`);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add to DML');
    } finally {
      setAddingToDml(null);
    }
  };

  // ── Bulk add all directory files to DML ──
  const [bulkAdding, setBulkAdding] = useState(null);

  const addAllToDml = async () => {
    const targets = unmatchedDirFiles;
    if (!projectId || targets.length === 0) return;
    setBulkAdding({ done: 0, total: targets.length });
    const failures = [];
    let added = 0;
    let dmlId = null;
    try {
      dmlId = await ensureDml();
    } catch {
      toast.error('Could not load or create the project DML');
      setBulkAdding(null);
      return;
    }
    const seen = new Set();
    for (let i = 0; i < targets.length; i++) {
      const entry = targets[i];
      try {
        const { payload, error } = await buildEntryPayload(entry);
        if (!payload) {
          failures.push(`${entry.fileMatch.name}: ${error}`);
        } else {
          const key = payload.entry_type === 'pm' ? payload.pm_code
            : payload.entry_type === 'ddn' ? payload.ddn_code
            : Object.values(payload.dmc).join('-');
          if (seen.has(key)) {
            failures.push(`${entry.fileMatch.name}: duplicate code of another file in this batch`);
          } else {
            await axios.post(`${API}/dml/${dmlId}/entry`, payload, { headers });
            seen.add(key);
            added++;
          }
        }
      } catch (err) {
        failures.push(`${entry.fileMatch.name}: ${err.response?.data?.detail || 'request failed'}`);
      }
      setBulkAdding({ done: i + 1, total: targets.length });
    }
    setBulkAdding(null);
    if (added > 0) toast.success(`Added ${added} of ${targets.length} files to DML`);
    if (failures.length > 0) {
      toast.warning(`${failures.length} file${failures.length !== 1 ? 's' : ''} could not be added`, {
        description: failures.slice(0, 5).join('\n') + (failures.length > 5 ? `\n…and ${failures.length - 5} more` : ''),
        duration: 10000,
      });
    }
    await fetchData();
  };

  const buildEntryPayload = async (entry) => {
    const file = await entry.fileMatch.handle.getFile();
    const text = await file.text();
    const doc = new DOMParser().parseFromString(text, 'application/xml');
    const issueInfo = doc.querySelector('issueInfo');
    const secEl = doc.querySelector('security');
    const dmCodeEl = doc.querySelector('dmIdent dmCode');
    const pmCodeEl = doc.querySelector('pmIdent pmCode');
    const ddnCodeEl = doc.querySelector('ddnIdent ddnCode');
    if (dmCodeEl) {
      const attr = (n) => dmCodeEl.getAttribute(n) || '';
      const techName = doc.querySelector('dmTitle techName')?.textContent?.trim() || '';
      const infoName = doc.querySelector('dmTitle infoName')?.textContent?.trim() || '';
      return { payload: {
        dmc: {
          model_ident_code: attr('modelIdentCode'), system_diff_code: attr('systemDiffCode'),
          system_code: attr('systemCode'), sub_system_code: attr('subSystemCode'),
          sub_sub_system_code: attr('subSubSystemCode'), assy_code: attr('assyCode'),
          disassy_code: attr('disassyCode'), disassy_code_variant: attr('disassyCodeVariant'),
          info_code: attr('infoCode'), info_code_variant: attr('infoCodeVariant'),
          item_location_code: attr('itemLocationCode'),
          learn_code: attr('learnCode'), learn_event_code: attr('learnEventCode'),
        },
        title: (infoName ? `${techName} - ${infoName}` : techName) || entry.fileMatch.name,
        issue_number: issueInfo?.getAttribute('issueNumber') || '001',
        in_work: issueInfo?.getAttribute('inWork') || '00',
        security_classification: secEl?.getAttribute('securityClassification') || '01',
        schema: entry.detectedSchema || '',
        responsible_partner_company: '', originator: '',
      } };
    }
    if (pmCodeEl) {
      const attr = (n) => pmCodeEl.getAttribute(n) || '';
      const pmTitle = doc.querySelector('pmTitle')?.textContent?.trim() || entry.fileMatch.name;
      return { payload: {
        dmc: {
          model_ident_code: attr('modelIdentCode'), system_diff_code: 'A',
          system_code: '00', sub_system_code: '0', sub_sub_system_code: '0',
          assy_code: '00', disassy_code: '00', disassy_code_variant: 'A',
          info_code: '00S', info_code_variant: 'A', item_location_code: 'D',
          learn_code: '', learn_event_code: '',
        },
        title: pmTitle,
        issue_number: issueInfo?.getAttribute('issueNumber') || '001',
        in_work: issueInfo?.getAttribute('inWork') || '00',
        security_classification: secEl?.getAttribute('securityClassification') || '01',
        schema: 'Publication Module',
        responsible_partner_company: '', originator: '',
        entry_type: 'pm',
        pm_code: `PMC-${attr('modelIdentCode')}-${attr('pmIssuer')}-${attr('pmNumber')}-${attr('pmVolume')}`,
      } };
    }
    if (ddnCodeEl) {
      const attr = (n) => ddnCodeEl.getAttribute(n) || '';
      return { payload: {
        dmc: {
          model_ident_code: attr('modelIdentCode'), system_diff_code: 'A',
          system_code: '00', sub_system_code: '0', sub_sub_system_code: '0',
          assy_code: '00', disassy_code: '00', disassy_code_variant: 'A',
          info_code: '00A', info_code_variant: 'A', item_location_code: 'D',
          learn_code: '', learn_event_code: '',
        },
        title: entry.fileMatch.name.replace(/\.xml$/i, ''),
        issue_number: '001', in_work: '00',
        security_classification: secEl?.getAttribute('securityClassification') || '01',
        schema: 'Data Dispatch Note',
        responsible_partner_company: '', originator: '',
        entry_type: 'ddn',
        ddn_code: `DDN-${attr('modelIdentCode')}-${attr('senderIdent')}-${attr('receiverIdent')}-${attr('yearOfDataIssue')}-${attr('seqNumber')}`,
      } };
    }
    return { payload: null, error: 'no dmCode, pmCode or ddnCode found in XML' };
  };

  const dmCount = dmlEntries.length;

  // ── Open editor for entry ──
  const openEditor = async (entry) => {
    setEditingEntry(entry);
    setCollapsedSections({ close: true });

    // Map detected schema label to editor schema type
    const mapSchemaToType = (label) => {
      if (!label) return null;
      const lower = label.toLowerCase();
      if (lower === 'descriptive') return 'descriptive';
      if (lower === 'procedural') return 'procedural';
      if (lower === 'crew') return 'crew';
      if (lower === 'publication module') return 'pm';
      return null;
    };

    // If there's authored content in DB, load it
    if (entry.authored) {
      const mod = entry.authored;
      setDmc(mod.dmc || entry.dmc_formatted);
      setTitleField(mod.title || entry.title);
      setSchemaType(mod.schema_type || 'descriptive');
      setSecurity(mod.security || '01');
      setRpc(mod.rpc || '');
      setIssueNumber(mod.issue_number || '001');
      setInWork(mod.in_work || '00');
      setQa(mod.quality_assurance || 'Unverified');
      {
        const _lps = normalizeLpIds(mod.levelled_paras || [], (mod.schema_type || 'descriptive') === 'procedural' ? 'stp' : 'par');
        setLevelledParas((mod.schema_type || 'descriptive') === 'crew' ? normalizeLpCrewIds(_lps) : _lps);
      }
      // Backfill missing ident data (part numbers, mfr codes) from the directory file
      let prelim = mod.preliminary_rqmts || null;
      let mergedFileIds = [];
      if (entry.fileMatch) {
        try {
          const parsedFile = await parseXmlFile(entry.fileMatch.handle);
          mergedFileIds = parsedFile?.fileIds || [];
          if (parsedFile?.preliminaryRqmts) {
            if (!prelim) {
              prelim = parsedFile.preliminaryRqmts;
            } else {
              const fillFrom = (dbArr = [], fileArr = []) => dbArr.map((item, i) => {
                const match = fileArr.find(f => (item.id && f.id === item.id) || (item.name && f.name === item.name)) || fileArr[i];
                if (!match) return item;
                return { ...item, pno: item.pno || match.pno || '', mfc: item.mfc || match.mfc || '' };
              });
              prelim = {
                ...prelim,
                support_equips: fillFrom(prelim.support_equips, parsedFile.preliminaryRqmts.support_equips),
                supplies: fillFrom(prelim.supplies, parsedFile.preliminaryRqmts.supplies),
                spares: fillFrom(prelim.spares, parsedFile.preliminaryRqmts.spares),
                persons: (prelim.persons?.length ? prelim.persons : parsedFile.preliminaryRqmts.persons) || [],
                tech_infos: (prelim.tech_infos?.length ? prelim.tech_infos : parsedFile.preliminaryRqmts.tech_infos) || [],
              };
            }
          }
        } catch { /* file unreadable — keep DB content */ }
      }
      setPreliminaryRqmts(prelim);
      setFileIds(mergedFileIds);
      setCloseRqmts(mod.close_rqmts || null);
      setCrewType(mod.crew_type || 'crewRefCard');
      setCrewTitle(mod.crew_title || '');
      setCrewDrills(normalizeCrewIds(mod.crew_drills || []));
      setPmEntries(mod.pm_entries || []);
      setFileXmlContent(null);
    } else if (entry.fileMatch) {
      // Parse XML from working directory file
      const parsed = await parseXmlFile(entry.fileMatch.handle);
      if (parsed) {
        setDmc(entry.dmc_formatted);
        setTitleField(parsed.meta.title || entry.title);
        setSchemaType(parsed.detectedSchema || 'descriptive');
        setSecurity(parsed.meta.security);
        setRpc(parsed.meta.rpc);
        setIssueNumber(parsed.meta.issue_number);
        setInWork(parsed.meta.in_work);
        setQa(parsed.meta.quality_assurance || 'Unverified');
        setLevelledParas((parsed.detectedSchema === 'crew') ? normalizeLpCrewIds(parsed.levelledParas) : parsed.levelledParas);
        setPreliminaryRqmts(parsed.preliminaryRqmts || null);
        setCloseRqmts(parsed.closeRqmts || null);
        if (parsed.crewData) {
          setCrewType(parsed.crewData.crewType || 'crewRefCard');
          setCrewTitle(parsed.crewData.crewTitle || '');
          setCrewDrills(normalizeCrewIds(parsed.crewData.crewDrills || []));
        } else {
          setCrewType('crewRefCard');
          setCrewTitle('');
          setCrewDrills([]);
        }
        setPmEntries(parsed.pmEntries || []);
        setFileXmlContent(parsed.rawXml);
        setFileIds(parsed.fileIds || []);
      } else {
        // Fallback if parsing fails
        setDmc(entry.dmc_formatted);
        setTitleField(entry.title);
        const fallbackType = mapSchemaToType(entry.detectedSchema)
          || (entry.schema?.toLowerCase().includes('procedural') ? 'procedural' : 'descriptive');
        setSchemaType(fallbackType);
        setSecurity('01'); setRpc(''); setIssueNumber('001'); setInWork('00'); setQa('Unverified');
        setLevelledParas([]);
        setPreliminaryRqmts(null);
        setCloseRqmts(null);
        setCrewType('crewRefCard'); setCrewTitle(''); setCrewDrills([]);
        setPmEntries([]);
        setFileXmlContent(null);
      }
    } else {
      // New / empty module
      setDmc(entry.dmc_formatted);
      setTitleField(entry.title);
      const detectedType = mapSchemaToType(entry.detectedSchema) || mapSchemaToType(entry.schema)
        || (entry.schema?.toLowerCase().includes('procedural') ? 'procedural'
          : entry.schema?.toLowerCase().includes('crew') ? 'crew' : 'descriptive');
      setSchemaType(detectedType);
      setSecurity('01'); setRpc(''); setIssueNumber('001'); setInWork('00'); setQa('Unverified');
      setLevelledParas([]);
      setPreliminaryRqmts(detectedType === 'procedural' ? { conditions: [], support_equips: [], supplies: [], spares: [], safety: [] } : null);
      setCloseRqmts(detectedType === 'procedural' ? { conditions: [] } : null);
      setCrewType('crewRefCard'); setCrewTitle(''); setCrewDrills([]);
      setPmEntries([]);
      setFileXmlContent(null);
      setFileIds([]);
    }

    // Snapshot for unsaved tracking
    setTimeout(() => {
      const mod = entry.authored;
      setSavedSnapshot(JSON.stringify({
        dmc: mod?.dmc || entry.dmc_formatted,
        titleField: mod?.title || entry.title,
        schemaType: mod?.schema_type || 'descriptive',
        security: mod?.security || '01',
        rpc: mod?.rpc || '',
        issueNumber: mod?.issue_number || '001',
        inWork: mod?.in_work || '00',
        qa: mod?.quality_assurance || 'Unverified',
        levelledParas: mod?.levelled_paras || [],
        crewType: mod?.crew_type || 'crewRefCard',
        crewTitle: mod?.crew_title || '',
        crewDrills: mod?.crew_drills || [],
        pmEntries: mod?.pm_entries || [],
      }));
    }, 100);

    setView('editor');
  };

  // ── Save module ──
  const saveModule = async () => {
    if (!dmc || !titleField) { toast.error('DMC and Title are required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/author/module`, {
        project_id: projectId, dmc, title: titleField,
        schema_type: schemaType, content_blocks: [],
        levelled_paras: levelledParas,
        preliminary_rqmts: schemaType === 'procedural' ? preliminaryRqmts : null,
        close_rqmts: schemaType === 'procedural' ? closeRqmts : null,
        crew_type: schemaType === 'crew' ? crewType : null,
        crew_title: schemaType === 'crew' ? crewTitle : '',
        crew_drills: schemaType === 'crew' ? crewDrills : [],
        pm_entries: schemaType === 'pm' ? pmEntries : [],
        security, rpc, issue_number: issueNumber,
        in_work: inWork, quality_assurance: qa,
      }, { headers });
      toast.success('Module saved');
      setSavedSnapshot(JSON.stringify({ dmc, titleField, schemaType, security, rpc, issueNumber, inWork, qa, levelledParas, crewType, crewTitle, crewDrills, pmEntries }));
      setHasUnsaved(false);
      fetchData();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  // ── Export XML ──
  const exportXml = async () => {
    const mod = modules.find(m => m.dmc === dmc);
    if (!mod) { toast.error('Save the module first before exporting'); return; }
    try {
      const res = await axios.post(`${API}/author/module/${mod.id}/export`, {}, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `${dmc || 'module'}.XML`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('XML exported');
    } catch { toast.error('Export failed'); }
  };

  const addLevelledPara = () => {
    setLevelledParas(prev => [...prev, {
      id: nextSeqId(schemaType === 'procedural' ? 'stp' : 'par', prev), title: '', elements: [{ id: uid(), type: 'para', content: '' }]
    }]);
  };

  const updateLP = (idx, lp) => { setLevelledParas(prev => { const next = prev.map((p, i) => i === idx ? lp : p); return schemaType === 'crew' ? normalizeLpCrewIds(next) : next; }); };
  const deleteLP = (idx) => { setLevelledParas(prev => prev.filter((_, i) => i !== idx)); };

  const dragLpIdx = useRef(null);
  const [xmlJump, setXmlJump] = useState(null);
  const moveLP = (from, to) => {
    if (from === null || from === undefined || from === to) return;
    setLevelledParas(prev => {
      if (to < 0 || to >= prev.length || from < 0 || from >= prev.length) return prev;
      const arr = [...prev];
      const [item] = arr.splice(from, 1);
      arr.splice(to, 0, item);
      return renumberSpecIds(arr, schemaType === 'procedural' ? 'stp' : 'par');
    });
  };

  const backToList = () => {
    if (hasUnsaved && !window.confirm('You have unsaved changes. Discard?')) return;
    setView('list');
    setEditingEntry(null);
    setFileXmlContent(null);
  };

  // ═══════════════════════════════════════
  // RENDER: NO PROJECT SELECTED
  // ═══════════════════════════════════════
  if (!projectId) {
    return (
      <div className="space-y-6" data-testid="author-page">
        <div className="bg-slate-900 text-white px-6 py-3 flex items-center justify-between -mx-6 -mt-6 mb-6 rounded-t-lg">
          <Button variant="ghost" onClick={() => navigate(projectId ? `/dashboard/projects/${projectId}` : '/dashboard/projects')} className="text-white hover:bg-slate-800 hover:text-white" data-testid="back-to-tools-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> {projectId ? 'Back to Project' : 'Back to Projects'}
          </Button>
        </div>
        <div className="max-w-xl mx-auto text-center space-y-6 py-12">
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Author Tool</h1>
          <p className="text-slate-500">Select a project and data module to create or update content</p>
          <Select value="none" onValueChange={v => setSelectedProject(v)}>
            <SelectTrigger className="w-[320px] mx-auto" data-testid="author-project-select">
              <SelectValue placeholder="Select a project..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none" disabled>Select a project...</SelectItem>
              {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════
  // RENDER: EDITOR VIEW
  // ═══════════════════════════════════════
  if (view === 'editor' && editingEntry) {
    return (
      <div className="space-y-0" data-testid="author-page">
        {/* Toolbar */}
        <div className="bg-slate-900 text-white px-6 py-2.5 flex items-center justify-between -mx-6 -mt-6 mb-4 rounded-t-lg" data-testid="author-toolbar">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={backToList} className="text-white hover:bg-slate-800 hover:text-white" data-testid="back-to-list-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <span className="text-slate-500">|</span>
            <span className="text-sm text-slate-300">Author</span>
            <span className="text-slate-500">/</span>
            <span className="text-sm text-white font-mono truncate max-w-[300px]">{dmc}</span>
          </div>
          <div className="flex items-center gap-2">
            {hasUnsaved && (
              <Badge className="bg-amber-600 text-white text-xs" data-testid="unsaved-badge">Unsaved changes</Badge>
            )}
            <Button size="sm" onClick={saveModule} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid="save-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
            </Button>
            {dirHandle && (
              <Button variant="ghost" size="sm" className="text-white hover:bg-slate-800 gap-1.5" data-testid="import-dir-btn" title="Import from Working Dir">
                <FolderOpen className="w-3.5 h-3.5" /> Import
              </Button>
            )}
            <Button variant="ghost" size="sm" className="text-white hover:bg-slate-800" onClick={() => window.print()} title="Print" data-testid="print-btn">
              <Printer className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="sm" className="text-white hover:bg-slate-800 gap-1.5" onClick={exportXml} data-testid="export-xml-btn">
              <Download className="w-3.5 h-3.5" /> XML Export
            </Button>
          </div>
        </div>

        {/* Split layout: Editor (left) | XML Preview (right) */}
        <div className="grid grid-cols-12 gap-4" style={{ height: 'calc(100vh - 120px)' }}>
          {/* Left: Form + Content */}
          <div className="col-span-7 overflow-y-auto pr-2 space-y-4">
            {/* DMC Code */}
            <h2 className="text-xl font-bold font-mono text-violet-700" data-testid="editor-dmc">{(editingEntry?.entry_type === 'pm' && editingEntry?.pm_code) ? editingEntry.pm_code : (editingEntry?.entry_type === 'ddn' && editingEntry?.ddn_code) ? editingEntry.ddn_code : dmc}</h2>

            {/* Identification and Status */}
            <Card className="border-slate-200">
              <CardContent className="p-4 space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Identification and Status</h3>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                  <div>
                    <Label className="text-xs text-slate-500">Title</Label>
                    <p className="text-sm font-medium text-slate-800">{titleField}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Schema</Label>
                    <Select value={schemaType} onValueChange={(v) => {
                      setSchemaType(v);
                      if (v === 'procedural' && !preliminaryRqmts) setPreliminaryRqmts({ conditions: [], support_equips: [], supplies: [], spares: [], safety: [] });
                      if (v === 'procedural' && !closeRqmts) setCloseRqmts({ conditions: [] });
                    }}>
                      <SelectTrigger className="h-8 text-sm" data-testid="schema-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="descriptive">Descriptive</SelectItem>
                        <SelectItem value="procedural">Procedural</SelectItem>
                        <SelectItem value="crew">Crew</SelectItem>
                        <SelectItem value="pm">Publication Module</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Security</Label>
                    <Input value={security} onChange={e => setSecurity(e.target.value)} className="h-8 text-sm" data-testid="security-input" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">RPC</Label>
                    <Input value={rpc} onChange={e => setRpc(e.target.value)} placeholder="Responsible Partner Company" className="h-8 text-sm" data-testid="rpc-input" />
                  </div>
                  <div className="flex gap-3">
                    <div className="space-y-1 flex-1">
                      <Label className="text-xs text-slate-500">Issue Number</Label>
                      <Input value={issueNumber} onChange={e => setIssueNumber(e.target.value)} className="h-8 text-sm font-mono" data-testid="issue-number-input" />
                    </div>
                    <div className="space-y-1 flex-1">
                      <Label className="text-xs text-slate-500">In Work</Label>
                      <Input value={inWork} onChange={e => setInWork(e.target.value)} className="h-8 text-sm font-mono" data-testid="in-work-input" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Quality Assurance</Label>
                    <Select value={qa} onValueChange={setQa}>
                      <SelectTrigger className="h-8 text-sm" data-testid="qa-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {QA_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* S1000D Version */}
                <div className="flex items-center gap-2 pt-1">
                  <Badge variant="outline" className="text-xs">S1000D {currentProject?.s1000d_version || 'Issue 5.0'}</Badge>
                </div>
              </CardContent>
            </Card>

            {/* Checks */}
            <InlineChecks projectId={projectId} levelledParas={levelledParas} token={token} getModulePayload={() => ({
              project_id: projectId,
              dmc, title: titleField, schema_type: schemaType,
              levelled_paras: levelledParas,
              preliminary_rqmts: schemaType === 'procedural' ? preliminaryRqmts : null,
              close_rqmts: schemaType === 'procedural' ? closeRqmts : null,
              crew_type: schemaType === 'crew' ? crewType : null,
              crew_title: schemaType === 'crew' ? crewTitle : '',
              crew_drills: schemaType === 'crew' ? crewDrills : [],
              pm_entries: schemaType === 'pm' ? pmEntries : [],
              content_blocks: [],
              security, rpc, issue_number: issueNumber, in_work: inWork, quality_assurance: qa,
            })} onJumpToError={(err) => setXmlJump({ path: err.path, line: err.line, nonce: Date.now() })} />

            {/* Broken internal references warning */}
            {brokenRefs.length > 0 && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm" data-testid="broken-refs-warning">
                <p className="font-medium text-amber-800 flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4" />
                  {brokenRefs.length} internal reference{brokenRefs.length !== 1 ? 's' : ''} point{brokenRefs.length === 1 ? 's' : ''} to missing id{brokenRefs.length !== 1 ? 's' : ''}
                </p>
                <div className="mt-1 space-y-0.5">
                  {brokenRefs.slice(0, 6).map((b, i) => (
                    <p key={i} className="text-amber-700 text-xs">
                      <span className="font-mono">[Ref: {b.refId}]</span> in <span className="font-medium">{b.section}</span> — target id not found in this module
                    </p>
                  ))}
                  {brokenRefs.length > 6 && <p className="text-amber-600 text-xs">…and {brokenRefs.length - 6} more</p>}
                </div>
              </div>
            )}

            {/* Preliminary Requirements (Procedural only) */}
            {schemaType === 'procedural' && (
              <div onFocusCapture={() => setActiveSection('prelim')} onClickCapture={() => setActiveSection('prelim')}>
              <PreliminaryRqmtsEditor
                data={preliminaryRqmts || { conditions: [], support_equips: [], supplies: [], spares: [], safety: [] }}
                onChange={setPreliminaryRqmts}
                dirFiles={dirFiles}
                currentDmc={dmc}
                dmlEntries={dmlEntries}
                collapsed={!!collapsedSections['prelim']}
                onToggleCollapse={() => toggleSection('prelim')}
              />
              </div>
            )}

            {/* Crew Type Selector (Crew only) */}
            {schemaType === 'crew' && (
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <CrewTypeSelector crewType={crewType} onChange={setCrewType} />
                </CardContent>
              </Card>
            )}

            {/* Main Content — Description, Procedure, Crew, or Publication Module */}
            {schemaType === 'pm' ? (
              <PmEditor
                pmEntries={pmEntries}
                onPmEntriesChange={setPmEntries}
                dmlEntries={dmlEntries}
                dirFiles={dirFiles}
              />
            ) : schemaType === 'crew' && crewType === 'crewRefCard' ? (
              <CrewRefCardEditor
                crewTitle={crewTitle}
                crewDrills={crewDrills}
                onTitleChange={setCrewTitle}
                onDrillsChange={(d) => setCrewDrills(normalizeCrewIds(d))}
                dmlEntries={dmlEntries}
                dirFiles={dirFiles}
                dirIcns={dirIcns}
                internalTargets={internalTargets}
                collapsedSections={collapsedSections}
                onToggleSection={toggleSection}
              />
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {schemaType === 'procedural' ? 'Main Procedure' : schemaType === 'crew' ? 'Descriptive Crew Content' : 'Description'}
                  </h3>
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={addLevelledPara} data-testid="add-levelled-para-btn">
                    <Plus className="w-3.5 h-3.5" /> {schemaType === 'procedural' ? 'Add Step' : 'Add levelledPara'}
                  </Button>
                </div>

                {levelledParas.length === 0 ? (
                  <Card className="border-dashed border-2 border-slate-200">
                    <CardContent className="p-10 text-center">
                      <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500 text-sm">No content yet</p>
                      <p className="text-slate-400 text-xs mt-1">Click &apos;Add levelledPara&apos; to start authoring</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {levelledParas.map((lp, i) => (
                      <div
                        key={lp.id || i}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => { e.preventDefault(); moveLP(dragLpIdx.current, i); dragLpIdx.current = null; }}
                        data-testid={`lp-drop-zone-${i}`}
                      >
                        <LevelledParaBlock lp={lp} index={i} count={levelledParas.length} onMoveUp={() => moveLP(i, i - 1)} onMoveDown={() => moveLP(i, i + 1)} dragHandleProps={{ draggable: true, onDragStart: (e) => { dragLpIdx.current = i; e.dataTransfer.effectAllowed = 'move'; } }} onUpdate={lp2 => updateLP(i, lp2)} onDelete={() => deleteLP(i)} isProcedural={schemaType === 'procedural'} isCrew={schemaType === 'crew'} dmlEntries={dmlEntries} dirFiles={dirFiles} dirIcns={dirIcns} internalTargets={internalTargets} irttOptions={irttOptions} allocateId={(prefix) => nextSeqId(prefix, levelledParas)} onActive={() => setActiveSection(`lp-${lp.id || i}`)} collapsed={!!collapsedSections[`lp-${lp.id || i}`]} onToggleCollapse={() => toggleSection(`lp-${lp.id || i}`)} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Close Requirements (Procedural only) */}
            {schemaType === 'procedural' && (
              <div onFocusCapture={() => setActiveSection('close')} onClickCapture={() => setActiveSection('close')}>
              <CloseRqmtsEditor
                data={closeRqmts || { conditions: [] }}
                onChange={setCloseRqmts}
                dirFiles={dirFiles}
                currentDmc={dmc}
                dmlEntries={dmlEntries}
                collapsed={!!collapsedSections['close']}
                onToggleCollapse={() => toggleSection('close')}
              />
              </div>
            )}
          </div>

          {/* Right: XML Preview */}
          <div className="col-span-5 overflow-hidden">
            <XmlPreviewPanel
              projectId={projectId}
              dmc={dmc}
              title={titleField}
              schemaType={schemaType}
              levelledParas={levelledParas}
              preliminaryRqmts={preliminaryRqmts}
              closeRqmts={closeRqmts}
              crewType={crewType}
              crewTitle={crewTitle}
              crewDrills={crewDrills}
              pmEntries={pmEntries}
              security={security}
              rpc={rpc}
              issueNumber={issueNumber}
              inWork={inWork}
              qa={qa}
              token={token}
              collapsedSections={collapsedSections}
              activeKey={activeSection}
              jump={xmlJump}
            />
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════
  // RENDER: LIST VIEW (DML entries table)
  // ═══════════════════════════════════════
  return (
    <div className="space-y-0" data-testid="author-page">
      {/* Sticky header block: everything down to the filter bar stays visible on scroll */}
      <div className="sticky top-16 z-30 bg-slate-50 -mx-6 px-6 -mt-6 pb-4 border-b border-slate-200/70 shadow-sm" data-testid="author-sticky-header">
      {/* Header */}
      <div className="bg-slate-900 text-white px-6 py-3 flex items-center justify-between -mx-6 mb-6 rounded-t-lg">
        <Button variant="ghost" onClick={() => navigate(projectId ? `/dashboard/projects/${projectId}` : '/dashboard/projects')} className="text-white hover:bg-slate-800 hover:text-white" data-testid="back-to-tools-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Project
        </Button>
        {currentProject && (
          <span className="text-sm text-slate-300">Project: <span className="font-semibold text-white">{currentProject.title}</span></span>
        )}
      </div>

      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Author Tool</h1>
            <p className="text-sm text-slate-500 mt-1">Select a project and data module to create or update content</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedProject} onValueChange={v => { setSelectedProject(v); setView('list'); }}>
              <SelectTrigger className="w-[240px]" data-testid="author-project-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
              </SelectContent>
            </Select>
            {projectId && (dirName ? (
              <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 gap-1.5 max-w-[240px]" data-testid="wd-chip">
                <FolderOpen className="w-3 h-3 shrink-0" /><span className="truncate font-mono text-xs">{dirName}</span>
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 gap-1.5" data-testid="wd-chip">
                <FolderOpen className="w-3 h-3" /><span className="text-xs">No directory linked</span>
              </Badge>
            ))}
            <Badge className="bg-violet-100 text-violet-700 whitespace-nowrap">{dmCount} Data Modules</Badge>
          </div>
        </div>

        {/* Working directory controls */}
        <div className="flex items-center gap-2 flex-wrap">
          {!dirHandle ? (
            <Button variant="outline" size="sm" onClick={linkDirectory} data-testid="link-dir-btn">
              <FolderOpen className="w-4 h-4 mr-2" /> Link Working Directory
            </Button>
          ) : (
            <>
              <Badge variant="secondary" className="flex items-center gap-1.5 px-3 py-1.5 text-xs">
                <FolderSync className="w-3.5 h-3.5 text-violet-600" />
                <span className="font-mono">{dirName}</span>
                <span className="text-slate-400">({dirFiles.length} XML files)</span>
              </Badge>
              {dirFiles.length === 0 && (
                <Button variant="outline" size="sm" onClick={refreshDir} className="gap-1.5 text-blue-600 border-blue-200 hover:bg-blue-50" data-testid="regrant-dir-btn">
                  <RefreshCw className="w-3.5 h-3.5" /> Grant Access &amp; Scan
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={refreshDir} disabled={scanningDir} data-testid="refresh-dir-btn">
                {scanningDir ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              </Button>
              <Button variant="outline" size="sm" onClick={unlinkDirectory} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="unlink-dir-btn">
                <XIcon className="w-3.5 h-3.5" />
              </Button>
            </>
          )}
        </div>

        {/* Search + Directory files summary */}
        {(dmlEntries.length > 0 || unmatchedDirFiles.length > 0) && (
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="relative w-72">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter by code, title or schema..."
                className="pl-9 h-9"
                data-testid="author-dm-search"
              />
            </div>
            {dirHandle && dirFiles.length > 0 && (
              <div className="flex items-center gap-3 text-sm" data-testid="dir-summary">
                <Badge className="bg-emerald-100 text-emerald-700">
                  <CheckCircle className="w-3 h-3 mr-1" /> {enrichedEntries.filter(e => e.fileMatch).length} matched
                </Badge>
                {enrichedEntries.filter(e => !e.fileMatch).length > 0 && (
                  <Badge className="bg-amber-100 text-amber-700">
                    {enrichedEntries.filter(e => !e.fileMatch).length} missing in directory
                  </Badge>
                )}
                {unmatchedDirFiles.length > 0 && (
                  <Badge className="bg-blue-100 text-blue-700">
                    <FolderOpen className="w-3 h-3 mr-1" /> {unmatchedDirFiles.length} extra files in directory
                  </Badge>
                )}
                {unmatchedDirFiles.length > 0 && (
                  <Button size="sm" className="gap-1.5 h-8 bg-blue-600 hover:bg-blue-700"
                    onClick={addAllToDml}
                    disabled={bulkAdding !== null || addingToDml !== null}
                    data-testid="add-all-to-dml-top-btn"
                  >
                    {bulkAdding ? (
                      <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Adding {bulkAdding.done}/{bulkAdding.total}…</>
                    ) : (
                      <><Plus className="w-3.5 h-3.5" /> Add All to DML</>
                    )}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
        </div>
      </div>

      <div className="space-y-4 pt-4">
        {/* Data Modules Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : dmlEntries.length === 0 && unmatchedDirFiles.length === 0 ? (
              <div className="text-center py-16 px-6">
                <PenTool className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 font-medium">No data modules found</p>
                <p className="text-sm text-slate-400 mt-1">Add entries in the DML Builder first, or link a working directory containing XML files</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="author-dm-table">
                  <thead>
                    <tr className="bg-slate-100 border-b border-slate-200">
                      {dirHandle && dirFiles.length > 0 && (
                        <th className="w-10 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider px-2 py-3">File</th>
                      )}
                      <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider px-4 py-3">Data Module Code</th>
                      <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider px-4 py-3">Data Module Title</th>
                      <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider px-4 py-3">Schema</th>
                      <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider px-4 py-3">Status</th>
                      {dirHandle && dirFiles.length > 0 && (
                        <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider px-4 py-3">File Name</th>
                      )}
                      <th className="w-52 px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {search.trim() && visibleEntries.length === 0 && visibleDirFiles.length === 0 && (
                      <tr>
                        <td colSpan={dirHandle && dirFiles.length > 0 ? 7 : 5} className="px-4 py-10 text-center text-sm text-slate-400" data-testid="author-search-no-results">
                          No data modules match "{search.trim()}"
                        </td>
                      </tr>
                    )}
                    {visibleEntries.map((entry) => {
                      const hasContent = !!entry.authored;
                      const hasFile = !!entry.fileMatch;
                      let status = 'No Content';
                      let statusColor = 'text-amber-600 bg-amber-50';
                      if (hasContent) {
                        const lpCount = entry.authored.levelled_paras?.length || entry.authored.content_blocks?.length || 0;
                        status = `${lpCount} block${lpCount !== 1 ? 's' : ''}`;
                        statusColor = 'text-emerald-700 bg-emerald-50';
                      } else if (hasFile) {
                        status = 'In Directory';
                        statusColor = 'text-blue-700 bg-blue-50';
                      }

                      return (
                        <tr key={entry.idx} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${dirHandle && dirFiles.length > 0 && !hasFile && !hasContent ? 'bg-amber-50/30' : ''}`} data-testid={`dm-row-${entry.idx}`}>
                          {dirHandle && dirFiles.length > 0 && (
                            <td className="px-2 py-3 text-center">
                              {hasFile ? (
                                <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" />
                              ) : (
                                <XIcon className="w-4 h-4 text-amber-400 mx-auto" />
                              )}
                            </td>
                          )}
                          <td className="px-4 py-3">
                            <code className="text-sm font-mono text-violet-700">{entry.displayCode || entry.dmc_formatted}</code>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700">{entry.title}</td>
                          <td className="px-4 py-3">
                            {(() => {
                              const schemaLabel = entry.detectedSchema || entry.schema || null;
                              const supported = isSchemaSupported(schemaLabel);
                              if (!schemaLabel) return <Badge variant="outline" className="text-xs text-slate-400">-</Badge>;
                              return (
                                <Badge variant="outline" className={`text-xs ${supported ? 'border-violet-300 text-violet-700 bg-violet-50' : 'border-slate-200 text-slate-500'}`}>
                                  {schemaLabel}
                                </Badge>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={`text-xs ${statusColor}`}>{status}</Badge>
                          </td>
                          {dirHandle && dirFiles.length > 0 && (
                            <td className="px-4 py-3 text-xs font-mono text-slate-500">
                              {hasFile ? entry.fileMatch.name : <span className="text-amber-400 italic">Not found</span>}
                            </td>
                          )}
                          <td className="px-4 py-3">
                            {(() => {
                              const schemaLabel = entry.detectedSchema || entry.schema || null;
                              const supported = !schemaLabel || isSchemaSupported(schemaLabel);
                              const btnLabel = hasContent ? 'Edit' : hasFile ? 'Open' : 'Create';
                              return (
                                <Button size="sm" variant={hasContent ? 'outline' : 'default'}
                                  className={hasContent ? 'gap-1.5' : 'gap-1.5 bg-violet-600 hover:bg-violet-700'}
                                  onClick={() => openEditor(entry)}
                                  disabled={!supported}
                                  title={!supported ? `${schemaLabel} schema is not yet supported` : undefined}
                                  data-testid={`open-editor-${entry.idx}`}
                                >
                                  {btnLabel}
                                </Button>
                              );
                            })()}
                          </td>
                        </tr>
                      );
                    })}

                    {/* Unmatched files from working directory */}
                    {visibleDirFiles.length > 0 && (
                      <>
                        <tr className="bg-blue-50/50 border-b border-blue-200">
                          <td colSpan={dirHandle && dirFiles.length > 0 ? 7 : 5} className="px-4 py-2 text-xs font-semibold text-blue-600 uppercase tracking-wider">
                            <div className="flex items-center justify-between gap-4">
                              <div className="flex items-center gap-1.5">
                                <FolderOpen className="w-3.5 h-3.5" />
                                Additional files in working directory (not in DML)
                              </div>
                              <Button size="sm" variant="outline" className="gap-1.5 h-7 text-xs normal-case tracking-normal border-blue-300 text-blue-700 hover:bg-blue-100"
                                onClick={addAllToDml}
                                disabled={bulkAdding !== null || addingToDml !== null}
                                data-testid="add-all-to-dml-btn"
                              >
                                {bulkAdding ? (
                                  <><Loader2 className="w-3 h-3 animate-spin" /> Adding {bulkAdding.done}/{bulkAdding.total}…</>
                                ) : (
                                  <><Plus className="w-3 h-3" /> Add All to DML ({unmatchedDirFiles.length})</>
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                        {visibleDirFiles.map((entry) => {
                          const schemaLabel = entry.detectedSchema || null;
                          const supported = isSchemaSupported(schemaLabel);
                          return (
                          <tr key={`dir-${entry.idx}`} className="border-b border-slate-100 hover:bg-blue-50/30 transition-colors bg-blue-50/10" data-testid={`dir-file-${entry.idx}`}>
                            {dirHandle && dirFiles.length > 0 && (
                              <td className="px-2 py-3 text-center">
                                <FileText className="w-4 h-4 text-blue-400 mx-auto" />
                              </td>
                            )}
                            <td className="px-4 py-3">
                              <code className="text-sm font-mono text-blue-600">{entry.dmc_formatted}</code>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-500 italic">{entry.fileMatch.name}</td>
                            <td className="px-4 py-3">
                              {schemaLabel ? (
                                <Badge variant="outline" className={`text-xs ${supported ? 'border-violet-300 text-violet-700 bg-violet-50' : 'border-slate-200 text-slate-500'}`}>
                                  {schemaLabel}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs text-slate-400">-</Badge>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <Badge className="text-xs text-blue-700 bg-blue-50">From Directory</Badge>
                            </td>
                            {dirHandle && dirFiles.length > 0 && (
                              <td className="px-4 py-3 text-xs font-mono text-slate-500">{entry.fileMatch.name}</td>
                            )}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <Button size="sm" className="gap-1.5 bg-blue-600 hover:bg-blue-700"
                                  onClick={() => openEditor(entry)}
                                  disabled={!supported}
                                  title={!supported ? `${schemaLabel || 'Unknown'} schema is not yet supported` : undefined}
                                  data-testid={`open-dir-file-${entry.idx}`}
                                >
                                  Open
                                </Button>
                                <Button size="sm" variant="outline" className="gap-1.5 border-blue-300 text-blue-700 hover:bg-blue-50"
                                  onClick={() => addFileToDml(entry)}
                                  disabled={addingToDml !== null || bulkAdding !== null}
                                  title="Add this file as a DML entry"
                                  data-testid={`add-to-dml-${entry.idx}`}
                                >
                                  {addingToDml === entry.fileMatch.name ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                                  Add to DML
                                </Button>
                              </div>
                            </td>
                          </tr>
                          );
                        })}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuthorPage;
